import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    AlertCircle,
    Archive,
    Calendar,
    Check,
    Download,
    Edit,
    Eye,
    FileText,
    Folder,
    Image,
    MoreVertical,
    MessageSquare,
    RotateCcw,
    Save,
    Search,
    Send,
    Sparkles,
    Star,
    Trash2,
    Upload,
    Video,
    X,
} from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../api";
import {
    approveKmsContentRequest,
    archiveKmsContentRequest,
    createKmsContentCommentRequest,
    createKmsContentRequest,
    deleteKmsContentRequest,
    generateKmsArticleDraftRequest,
    getKmsArchivedContentsRequest,
    getKmsContentCommentsRequest,
    getKmsContentRatingSummaryRequest,
    getKmsContentRequest,
    getKmsContentSubscriptionRequest,
    getKmsContentWorkflowHistoryRequest,
    getKmsContentsRequest,
    getKmsContentsByRepertoireRequest,
    getKmsTrashedContentsRequest,
    hardDeleteKmsContentRequest,
    publishKmsContentRequest,
    rejectKmsContentRequest,
    restoreKmsContentFromArchiveRequest,
    restoreKmsContentFromTrashRequest,
    returnKmsContentToDraftRequest,
    submitKmsContentRequest,
    subscribeKmsContentRequest,
    unsubscribeKmsContentRequest,
    updateKmsContentRequest,
    upsertKmsContentRatingRequest,
} from "../../../redux/actions/kmsContentActions";
import {
    archiveKmsDocumentRequest,
    createKmsDocumentCommentRequest,
    deleteKmsDocumentRequest,
    getKmsDocumentCommentsRequest,
    getKmsArchivedDocumentsRequest,
    getKmsDocumentRequest,
    getKmsDocumentRatingSummaryRequest,
    getKmsDocumentSubscriptionRequest,
    getKmsDocumentsRequest,
    getKmsDocumentsByContentRequest,
    getKmsDocumentsByRepertoireRequest,
    getKmsTrashedDocumentsRequest,
    hardDeleteKmsDocumentRequest,
    approveKmsDocumentRequest,
    publishKmsDocumentRequest,
    rejectKmsDocumentRequest,
    restoreKmsDocumentFromArchiveRequest,
    restoreKmsDocumentFromTrashRequest,
    returnKmsDocumentToDraftRequest,
    submitKmsDocumentRequest,
    subscribeKmsDocumentRequest,
    unsubscribeKmsDocumentRequest,
    uploadKmsDocumentToContentRequest,
    uploadKmsDocumentToRepertoireRequest,
    upsertKmsDocumentRatingRequest,
    updateKmsDocumentRequest,
} from "../../../redux/actions/kmsDocumentActions";
import { searchKmsRequest } from "../../../redux/actions/kmsSearchActions";
import {
    createKmsCategoryRequest,
    createKmsTagRequest,
    deleteKmsCategoryRequest,
    deleteKmsTagRequest,
    getKmsCategoriesRequest,
    getKmsTagsRequest,
    getKmsTaxonomyOptionsRequest,
    toggleKmsCategoryActiveRequest,
    updateKmsCategoryRequest,
    updateKmsTagRequest,
} from "../../../redux/actions/kmsTaxonomyActions";
import {
    getAccessibleRepertoireTreeRequest,
} from "../../../redux/actions/repertoireActions";
import {
    KmsEmpty,
    KmsModal,
    KmsPageHeader,
    KmsStatusBadge,
    KmsTypeBadge,
} from "../shared/KmsShared";
import {
    CONTENT_TYPES,
    DOCUMENT_TYPES,
    contentTypeLabel,
    documentTypeLabel,
    flattenTree,
    formatBytes,
    formatDate,
    getContentTitle,
    getCurrentRole,
    getCurrentUser,
    getDocumentTitle,
    getDownloadUrl,
    getPreviewUrl,
    getRoleText,
    getUserPermissionForRepertoire,
    hasItemRight,
    isKnowledgeAdminRole,
    normalizeText,
    statusLabel,
} from "../shared/kmsUi";
import "../../Formation/ResponsableFormation/RessourcesStyles/TraitementDemandes.css";

const emptyContentForm = {
    title: "",
    summary: "",
    body: "",
    faqQuestion: "",
    faqAnswer: "",
    type: "ARTICLE",
    categoryId: "",
    tagIds: [],
};

const emptyDocForm = {
    title: "",
    description: "",
    documentType: "PDF",
    categoryId: "",
    tagIds: [],
    contentId: "",
    file: null,
};

const optionLabel = (item) => item?.name || item?.label || item?.title || "-";

const visibleForCollaborator = (item) => String(item?.status || "").toUpperCase() === "PUBLISHED";

const uniqById = (rows = []) => {
    const map = new Map();
    rows.forEach((item) => {
        if (item?.id !== undefined && item?.id !== null) map.set(String(item.id), item);
    });
    return Array.from(map.values());
};

const nodeChildren = (node) => node?.children || node?.subRepertoires || node?.subRepositories || [];
const itemRepertoireId = (item) =>
    item?.repertoireId ||
    item?.repositoryId ||
    item?.directoryId ||
    item?.repertoire?.id ||
    item?.repository?.id ||
    item?.directory?.id ||
    "";

const normalizeContentResult = (item) => ({
    ...item,
    id: item.id,
    title: item.title || item.name,
    type: item.type || item.contentType,
    status: item.status || item.contentStatus,
    summary: item.summary || item.snippet,
    repertoireId: itemRepertoireId(item),
    repertoireName: item.repertoireName || item.repositoryName || item.directoryName || item.repertoire?.name,
});

const normalizeDocumentResult = (item) => ({
    ...item,
    id: item.id,
    title: item.title || item.name,
    originalFilename: item.originalFilename || item.filename || item.name || item.title,
    documentType: item.documentType || item.fileType || item.type,
    status: item.status || item.documentStatus || item.contentStatus || item.publicationStatus,
    description: item.description || item.snippet,
    repertoireId: itemRepertoireId(item),
    repertoireName: item.repertoireName || item.repositoryName || item.directoryName || item.repertoire?.name,
    contentTitle: item.contentTitle || item.linkedContentTitle || item.content?.title,
});

const categoryIdOf = (item) => item?.category?.id || item?.categoryId || "";
const hasTagId = (item, tagId) => (item?.tags || []).some((tag) => String(tag.id || tag.tagId) === String(tagId));

const isDirectoryConsultable = (node, user) => {
    const permissions = node?.userPermissions || [];
    if (!permissions.length) return node?.canConsult !== false && node?.canView !== false;
    return getUserPermissionForRepertoire(node, user).canConsult;
};

const filterConsultableTree = (nodes = [], user) =>
    (nodes || [])
        .filter((node) => isDirectoryConsultable(node, user))
        .map((node) => ({ ...node, children: filterConsultableTree(nodeChildren(node), user) }));

const getRawRoleValue = (user, selectedRole) => {
    const role =
        selectedRole ||
        user?.role ||
        user?.roles?.[0] ||
        user?.authorities?.[0] ||
        user?.profil ||
        user?.profile ||
        "";

    if (!role) return "";

    if (typeof role === "string") {
        return role;
    }

    if (typeof role === "object") {
        return (
            role.name ||
            role.code ||
            role.roleName ||
            role.authority ||
            role.libelle ||
            role.label ||
            ""
        );
    }

    return "";
};

function KmsPageLoading({ message = "Chargement de votre espace de connaissance...", spinner = true }) {
    return (
        <div className="kms-page-loading">
            <div className="kms-loading-card">
                {spinner ? <div className="kms-spinner" /> : null}
                <p>{message}</p>
            </div>
        </div>
    );
}

const useKmsRole = () => {
    const role = useSelector(getCurrentRole);
    const user = useSelector(getCurrentUser);
    const loadingUser = useSelector((state) =>
        Boolean(
            state.auth?.loading ||
            state.user?.loading ||
            state.profile?.loading
        )
    );

    const rawRoleValue = getRawRoleValue(user, role);
    const normalizedRole = String(rawRoleValue || "").trim().toUpperCase();
    const roleText = getRoleText(rawRoleValue);

    return {
        role,
        user,
        currentUser: user,
        loadingUser,
        rawRoleValue,
        roleCode: normalizedRole,
        roleText,
        isRoleReady: Boolean(normalizedRole),
        isAdmin:
            normalizedRole.includes("ADMIN") ||
            normalizedRole.includes("SUPER_ADMIN") ||
            isKnowledgeAdminRole(rawRoleValue),
        isKnowledgeManager:
            normalizedRole.includes("RESPONSABLE_CONNAISSANCE") ||
            normalizedRole.includes("KNOWLEDGE_MANAGER"),
        isCollaborator:
            normalizedRole.includes("COLLABORATEUR") ||
            normalizedRole.includes("COLLABORATOR") ||
            normalizedRole.includes("EMPLOYEE") ||
            normalizedRole.includes("USER"),
    };
};

const useDirectoryWorkspace = () => {
    const dispatch = useDispatch();
    const rawTree = useSelector((state) => state.repertoire?.accessibleRepertoireTree || []);
    const user = useSelector(getCurrentUser);
    const tree = useMemo(() => filterConsultableTree(rawTree, user), [rawTree, user]);
    const loading = useSelector((state) => state.repertoire?.treeLoading);
    const [selected, setSelected] = useState(null);
    const [includeChildren, setIncludeChildren] = useState(false);

    useEffect(() => {
        dispatch(getAccessibleRepertoireTreeRequest());
        dispatch(getKmsContentsRequest({}));
        dispatch(getKmsDocumentsRequest({}));
    }, [dispatch]);

    useEffect(() => {
        if (selected || !tree?.length) return;
        setSelected(flattenTree(tree)[0] || null);
    }, [selected, tree]);

    return { tree, selected, setSelected, includeChildren, setIncludeChildren, loading };
};

const useKmsLibraryData = () => {
    const dispatch = useDispatch();
    const { isCollaborator } = useKmsRole();
    const tree = useSelector((state) => state.repertoire?.accessibleRepertoireTree || []);
    const contentState = useSelector((state) => state.kmsContent || {});
    const documentState = useSelector((state) => state.kmsDocument || {});
    const searchState = useSelector((state) => state.kmsSearch || {});
    const directoryIds = useMemo(() => getAllDirectoryIds(tree), [tree]);

    useEffect(() => {
        dispatch(getAccessibleRepertoireTreeRequest());
        dispatch(searchKmsRequest({ keyword: "", size: 200 }));
    }, [dispatch]);

    useEffect(() => {
        directoryIds.forEach((id) => {
            dispatch(getKmsContentsByRepertoireRequest(id));
            dispatch(getKmsDocumentsByRepertoireRequest(id));
        });
    }, [directoryIds.join(","), dispatch]);

    const contents = useMemo(() => {
        const accessibleIds = new Set(directoryIds.map(String));
        const byDirectory = directoryIds.flatMap((id) => contentState.byRepertoire?.[id] || []).map(normalizeContentResult);
        const globalRows = accessibleIds.size
            ? (contentState.items || []).map(normalizeContentResult).filter((item) => accessibleIds.has(String(itemRepertoireId(item))))
            : [];
        const searchRows = accessibleIds.size
            ? (searchState.items || [])
                .filter((item) => item.resultType !== "DOCUMENT")
                .map(normalizeContentResult)
                .filter((item) => accessibleIds.has(String(itemRepertoireId(item))))
            : [];
        return uniqById([...byDirectory, ...globalRows, ...searchRows]).filter((item) => !item.deleted && item.status !== "ARCHIVED" && (!isCollaborator || visibleForCollaborator(item)));
    }, [contentState.byRepertoire, contentState.items, directoryIds, searchState.items, isCollaborator]);

    const documents = useMemo(() => {
        const accessibleIds = new Set(directoryIds.map(String));
        const byDirectory = directoryIds.flatMap((id) => documentState.byRepertoire?.[id] || []).map(normalizeDocumentResult);
        const globalRows = accessibleIds.size
            ? (documentState.items || []).map(normalizeDocumentResult).filter((item) => accessibleIds.has(String(itemRepertoireId(item))))
            : [];
        const searchRows = accessibleIds.size
            ? (searchState.items || [])
                .filter((item) => item.resultType === "DOCUMENT")
                .map(normalizeDocumentResult)
                .filter((item) => accessibleIds.has(String(itemRepertoireId(item))))
            : [];
        return uniqById([...byDirectory, ...globalRows, ...searchRows]).filter((item) => !item.deleted && !item.archived && (!isCollaborator || visibleForCollaborator(item)));
    }, [directoryIds, documentState.byRepertoire, documentState.items, searchState.items, isCollaborator]);

    return { tree, directoryIds, contents, documents, contentState, documentState };
};

const getAllDirectoryIds = (tree) => flattenTree(tree || []).map((item) => item.id).filter(Boolean);

const getDescendantIds = (node) => {
    if (!node) return [];
    return [node.id, ...nodeChildren(node).flatMap(getDescendantIds)];
};

const getRepertoireNodeById = (tree, id) =>
    flattenTree(tree || []).find((item) => String(item.id) === String(id)) || null;

const getParentNodeForRepertoire = (tree, repertoireId) => {
    const findParent = (nodes = [], parent = null) => {
        for (const node of nodes) {
            if (String(node.id) === String(repertoireId)) return parent || node;
            const found = findParent(nodeChildren(node), node);
            if (found) return found;
        }
        return null;
    };
    return findParent(tree || []);
};

const getFinalRepertoire = (tree, parentId, childId) =>
    getRepertoireNodeById(tree, childId || parentId);

const filterByText = (rows, query, fields) => {
    const value = normalizeText(query);
    if (!value) return rows;
    return rows.filter((row) => normalizeText(fields.map((field) => field(row)).join(" ")).includes(value));
};

const documentIcon = (type) => {
    const value = String(type || "").toUpperCase();
    if (value === "IMAGE") return <Image size={30} />;
    if (value === "VIDEO") return <Video size={30} />;
    return <FileText size={30} />;
};

function SelectField({ label, value, onChange, children }) {
    return (
        <div className="kms-field">
            <label>{label}</label>
            <select value={value} onChange={(event) => onChange(event.target.value)}>
                {children}
            </select>
        </div>
    );
}

function TextField({ label, value, onChange, placeholder = "", full = false, textarea = false }) {
    return (
        <div className={`kms-field ${full ? "kms-field--full" : ""}`}>
            <label>{label}</label>
            {textarea ? (
                <textarea value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
            ) : (
                <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
            )}
        </div>
    );
}

function TagsSelect({ tags, value, onChange }) {
    const selected = new Set((value || []).map(String));
    return (
        <div className="kms-field">
            <label>Tags</label>
            <select
                multiple
                value={value || []}
                onChange={(event) =>
                    onChange(Array.from(event.target.selectedOptions).map((option) => option.value))
                }
            >
                {(tags || []).map((tag) => (
                    <option key={tag.id} value={tag.id}>
                        {optionLabel(tag)}
                    </option>
                ))}
            </select>
            {selected.size ? null : null}
        </div>
    );
}

function ContentFormModal({ open, onClose, content, tree, currentUser, isAdmin, categories, tags, saveLoading, initialRepertoire }) {
    const dispatch = useDispatch();
    const { generatedArticleDraft, articleDraftLoading } = useSelector((state) => state.kmsContent || {});
    const [form, setForm] = useState(emptyContentForm);
    const [parentRepertoireId, setParentRepertoireId] = useState("");
    const [subRepertoireId, setSubRepertoireId] = useState("");
    const [allowRepertoireChange, setAllowRepertoireChange] = useState(false);
    const isEdit = Boolean(content?.id);
    const parentOptions = useMemo(() => flattenTree(tree || []), [tree]);
    const parentRepertoire = useMemo(
        () => getRepertoireNodeById(tree, parentRepertoireId),
        [parentRepertoireId, tree]
    );
    const subOptions = nodeChildren(parentRepertoire);
    const finalRepertoire = useMemo(
        () => getFinalRepertoire(tree, parentRepertoireId, subRepertoireId),
        [parentRepertoireId, subRepertoireId, tree]
    );
    const finalPermissions = getUserPermissionForRepertoire(finalRepertoire, currentUser);
    const canCreateOnFinalRepertoire = Boolean(isAdmin || finalPermissions.canCreate);

    useEffect(() => {
        if (!open) return;
        const initialForm =
            content
                ? {
                    title: content.title || "",
                    summary: content.summary || "",
                    body: content.body || "",
                    faqQuestion: content.faqQuestion || "",
                    faqAnswer: content.faqAnswer || "",
                    type: content.type || "ARTICLE",
                    categoryId: content.category?.id || "",
                    tagIds: (content.tags || []).map((tag) => String(tag.id)),
                }
                : emptyContentForm;
        setForm(initialForm);
        setAllowRepertoireChange(false);
        const initialId = content?.repertoireId || initialRepertoire?.id;
        const parentNode = initialId ? getParentNodeForRepertoire(tree, initialId) : parentOptions[0];
        const currentNode = initialId ? getRepertoireNodeById(tree, initialId) : null;
        setParentRepertoireId(parentNode?.id ? String(parentNode.id) : "");
        setSubRepertoireId(currentNode && parentNode && String(currentNode.id) !== String(parentNode.id) ? String(currentNode.id) : "");
    }, [content, initialRepertoire, open, parentOptions, tree]);

    useEffect(() => {
        if (generatedArticleDraft && form.type === "ARTICLE") {
            setForm((prev) => ({ ...prev, body: generatedArticleDraft }));
        }
    }, [form.type, generatedArticleDraft]);

    const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const payload = {
        ...form,
        repertoireId: finalRepertoire?.id,
        family: "WEB_CONTENT",
        subType: form.type,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        tagIds: (form.tagIds || []).map(Number),
    };

    const handleSubmit = () => {
        if (!payload.repertoireId || !payload.title.trim() || !canCreateOnFinalRepertoire) return;
        if (isEdit) dispatch(updateKmsContentRequest(content.id, payload));
        else dispatch(createKmsContentRequest(payload));
        onClose();
    };

    const handleGenerate = () => {
        dispatch(
            generateKmsArticleDraftRequest({
                subject: form.title,
                context: form.summary,
                keywords: "",
                objective: "Créer un article KMS clair et structuré",
            })
        );
    };

    return (
        <KmsModal
            open={open}
            title={isEdit ? "Modifier le contenu" : "Créer un contenu"}
            onClose={onClose}
            footer={
                <>
                    <button className="kms-btn" type="button" onClick={onClose}>Annuler</button>
                    <button className="kms-btn kms-btn--primary" type="button" onClick={handleSubmit} disabled={saveLoading || !canCreateOnFinalRepertoire}>
                        <Save size={15} />
                        Enregistrer
                    </button>
                </>
            }
        >
            <div className="kms-form-grid">
                {initialRepertoire && !isEdit && !allowRepertoireChange ? (
                    <div className="kms-field kms-field--full">
                        <label>Répertoire</label>
                        <div className="kms-readonly-path">
                            <strong>{initialRepertoire.__path || initialRepertoire.path || initialRepertoire.name || optionLabel(initialRepertoire)}</strong>
                            <button className="kms-btn" type="button" onClick={() => setAllowRepertoireChange(true)}>Changer répertoire</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <SelectField
                            label="Répertoire parent"
                            value={parentRepertoireId}
                            onChange={(value) => {
                                setParentRepertoireId(value);
                                setSubRepertoireId("");
                            }}
                        >
                            <option value="">Sélectionner</option>
                            {parentOptions.map((item) => (
                                <option key={item.id} value={item.id}>{item.__path || optionLabel(item)}</option>
                            ))}
                        </SelectField>
                        <SelectField label="Sous-répertoire" value={subRepertoireId} onChange={setSubRepertoireId}>
                            <option value="">Aucun</option>
                            {subOptions.map((item) => (
                                <option key={item.id} value={item.id}>{optionLabel(item)}</option>
                            ))}
                        </SelectField>
                    </>
                )}
                {!canCreateOnFinalRepertoire ? (
                    <div className="kms-form-message kms-field--full">Création non autorisée sur ce répertoire</div>
                ) : null}
                <TextField label="Titre" value={form.title} onChange={(value) => patch("title", value)} />
                <SelectField label="Type" value={form.type} onChange={(value) => patch("type", value)}>
                    {CONTENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </SelectField>
                <TextField label="Résumé" value={form.summary} onChange={(value) => patch("summary", value)} full textarea />
                {form.type === "FAQ" ? (
                    <>
                        <TextField label="Question" value={form.faqQuestion} onChange={(value) => patch("faqQuestion", value)} full />
                        <TextField label="Réponse" value={form.faqAnswer} onChange={(value) => patch("faqAnswer", value)} full textarea />
                    </>
                ) : (
                    <TextField label="Contenu" value={form.body} onChange={(value) => patch("body", value)} full textarea />
                )}
                {form.type === "ARTICLE" ? (
                    <div className="kms-field kms-field--full">
                        <button className="kms-btn" type="button" onClick={handleGenerate} disabled={articleDraftLoading || !form.title.trim()}>
                            <Sparkles size={15} />
                            Générer avec IA
                        </button>
                    </div>
                ) : null}
                <SelectField label="Catégorie" value={form.categoryId} onChange={(value) => patch("categoryId", value)}>
                    <option value="">Aucune</option>
                    {(categories || []).map((category) => <option key={category.id} value={category.id}>{optionLabel(category)}</option>)}
                </SelectField>
                <TagsSelect tags={tags} value={form.tagIds} onChange={(value) => patch("tagIds", value)} />
            </div>
        </KmsModal>
    );
}

const getCommentAuthorName = (item) =>
    item?.authorFullName || item?.fullName || item?.createdByName || item?.createdByFullName || item?.authorName || "Utilisateur";

const getRatingValue = (summary) => Number(summary?.currentUserRating ?? summary?.myRating ?? 0);

const isSubscribed = (subscription) => Boolean(subscription?.subscribed);

const formatRatingSummary = (summary) => {
    const average = Number(summary?.averageRating || 0);
    const count = Number(summary?.ratingCount || 0);
    return `${average.toFixed(1)} / 5 (${count} évaluation${count > 1 ? "s" : ""})`;
};

function ContentDetailModal({ open, onClose, content }) {
    const dispatch = useDispatch();
    const { user, isAdmin } = useKmsRole();
    const {
        commentsByContent,
        subscriptionByContent,
        ratingSummaryByContent,
        workflowHistoryByContent,
    } = useSelector((state) => state.kmsContent || {});
    const linkedDocuments = useSelector((state) => state.kmsDocument?.byContent?.[content?.id] || []);
    const [comment, setComment] = useState("");

    useEffect(() => {
        if (!open || !content?.id) return;
        dispatch(getKmsDocumentsByContentRequest(content.id));
        dispatch(getKmsContentCommentsRequest(content.id));
        dispatch(getKmsContentSubscriptionRequest(content.id));
        dispatch(getKmsContentRatingSummaryRequest(content.id));
        dispatch(getKmsContentWorkflowHistoryRequest(content.id));
    }, [content?.id, dispatch, open]);

    if (!content) return null;
    const comments = commentsByContent?.[content.id] || [];
    const subscription = subscriptionByContent?.[content.id];
    const ratingSummary = ratingSummaryByContent?.[content.id];
    const workflow = workflowHistoryByContent?.[content.id] || [];

    const submitComment = () => {
        if (!comment.trim()) return;
        dispatch(createKmsContentCommentRequest(content.id, comment.trim()));
        setComment("");
    };

    return (
        <KmsModal open={open} title="Détail contenu" onClose={onClose}>
            <div className="kms-detail-grid">
                <div className="kms-detail-item"><span>Type</span><strong>{contentTypeLabel(content.type)}</strong></div>
                <div className="kms-detail-item"><span>Statut</span><strong>{statusLabel(content.status)}</strong></div>
                <div className="kms-detail-item"><span>Répertoire</span><strong>{content.repertoireName || "-"}</strong></div>
                <div className="kms-detail-item"><span>Auteur</span><strong>{content.createdByFullName || "-"}</strong></div>
                <div className="kms-detail-item"><span>Catégorie</span><strong>{optionLabel(content.category)}</strong></div>
                <div className="kms-detail-item"><span>Mise ? jour</span><strong>{formatDate(content.updatedAt)}</strong></div>
            </div>
            <div className="kms-preview">{content.type === "FAQ" ? `${content.faqQuestion || ""}\n\n${content.faqAnswer || ""}` : content.body || content.summary || "-"}</div>

            <div className="kms-card-header" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 14 }}>
                <h2>Documents liés</h2>
            </div>
            {linkedDocuments.length ? (
                <div className="kms-table-wrap">
                    <table className="kms-table">
                        <tbody>
                        {linkedDocuments.map((doc) => (
                            <tr key={doc.id}>
                                <td>{getDocumentTitle(doc)}</td>
                                <td><KmsTypeBadge>{documentTypeLabel(doc.documentType)}</KmsTypeBadge></td>
                                <td className="is-right">
                                    <button className="kms-icon-btn" type="button" onClick={() => openDocumentUrl(doc, "preview")}><Eye size={14} /></button>
                                    <button className="kms-icon-btn" type="button" onClick={() => openDocumentUrl(doc, "download")}><Download size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : <KmsEmpty>Aucun document lié</KmsEmpty>}

            <div className="kms-card-header" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 14 }}>
                <h2>Interaction</h2>
                <button
                    className="kms-btn"
                    type="button"
                    onClick={() => dispatch(isSubscribed(subscription) ? unsubscribeKmsContentRequest(content.id) : subscribeKmsContentRequest(content.id))}
                >
                    {isSubscribed(subscription) ? "Se désabonner" : "S'abonner"}
                </button>
            </div>
            <div className="kms-rating">
                <span>?valuation</span>
                <span>{formatRatingSummary(ratingSummary)}</span>
                {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                        key={rating}
                        className={`kms-star ${getRatingValue(ratingSummary) >= rating ? "is-active" : ""}`}
                        type="button"
                        onClick={() => dispatch(upsertKmsContentRatingRequest(content.id, rating))}
                    >
                        <Star size={14} />
                    </button>
                ))}
            </div>
            <div className="kms-field kms-field--full" style={{ marginTop: 12 }}>
                <label>Commentaires</label>
                <textarea value={comment} onChange={(event) => setComment(event.target.value)} />
                <button className="kms-btn" type="button" onClick={submitComment}>Commenter</button>
            </div>
            <div className="kms-comment-list">
                {comments.map((item) => (
                    <div className="kms-comment" key={item.id}>
                        <strong>{getCommentAuthorName(item)}</strong>
                        <span>{formatDate(item.createdAt)}</span>
                        <div>{item.message || item.comment || ""}</div>
                    </div>
                ))}
            </div>
            {/*{isAdmin && workflow.length ? (*/}
            {/*    <>*/}
            {/*        <div className="kms-card-header" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 14 }}>*/}
            {/*            <h2>Workflow</h2>*/}
            {/*        </div>*/}
            {/*        <div className="kms-comment-list">*/}
            {/*            {workflow.map((item) => (*/}
            {/*                <div className="kms-comment" key={item.id || `${item.action}-${item.createdAt}`}>*/}
            {/*                    <strong>{statusLabel(item.oldStatus)} â†’ {statusLabel(item.newStatus)}</strong>*/}
            {/*                    <div>{item.comment || item.action || "-"}</div>*/}
            {/*                </div>*/}
            {/*            ))}*/}
            {/*        </div>*/}
            {/*    </>*/}
            {/*) : null}*/}
        </KmsModal>
    );
}

function DocumentFormModal({ open, onClose, document, tree, currentUser, isAdmin, categories, tags, contents, saveLoading, initialRepertoire }) {
    const dispatch = useDispatch();
    const [form, setForm] = useState(emptyDocForm);
    const [parentRepertoireId, setParentRepertoireId] = useState("");
    const [subRepertoireId, setSubRepertoireId] = useState("");
    const [allowRepertoireChange, setAllowRepertoireChange] = useState(false);
    const isEdit = Boolean(document?.id);
    const parentOptions = useMemo(() => flattenTree(tree || []), [tree]);
    const parentRepertoire = useMemo(
        () => getRepertoireNodeById(tree, parentRepertoireId),
        [parentRepertoireId, tree]
    );
    const subOptions = nodeChildren(parentRepertoire);
    const finalRepertoire = useMemo(
        () => getFinalRepertoire(tree, parentRepertoireId, subRepertoireId),
        [parentRepertoireId, subRepertoireId, tree]
    );
    const finalPermissions = getUserPermissionForRepertoire(finalRepertoire, currentUser);
    const canCreateOnFinalRepertoire = Boolean(isAdmin || finalPermissions.canCreate);

    useEffect(() => {
        if (!open) return;
        const initialForm =
            document
                ? {
                    title: document.title || "",
                    description: document.description || "",
                    documentType: document.documentType || "PDF",
                    categoryId: document.category?.id || "",
                    tagIds: (document.tags || []).map((tag) => String(tag.id)),
                    contentId: document.contentId || "",
                    file: null,
                }
                : emptyDocForm;
        setForm(initialForm);
        setAllowRepertoireChange(false);
        const initialId = document?.repertoireId || initialRepertoire?.id;
        const parentNode = initialId ? getParentNodeForRepertoire(tree, initialId) : parentOptions[0];
        const currentNode = initialId ? getRepertoireNodeById(tree, initialId) : null;
        setParentRepertoireId(parentNode?.id ? String(parentNode.id) : "");
        setSubRepertoireId(currentNode && parentNode && String(currentNode.id) !== String(parentNode.id) ? String(currentNode.id) : "");
    }, [document, initialRepertoire, open, parentOptions, tree]);

    const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const payload = {
        ...form,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        tagIds: (form.tagIds || []).map(Number),
    };

    const handleSubmit = () => {
        if (isEdit) {
            if (!document?.id) return;
            dispatch(updateKmsDocumentRequest(document.id, payload));
            onClose();
            return;
        }
        if (!form.file || !canCreateOnFinalRepertoire) return;
        if (form.contentId) dispatch(uploadKmsDocumentToContentRequest(form.contentId, payload));
        else if (finalRepertoire?.id && canCreateOnFinalRepertoire) dispatch(uploadKmsDocumentToRepertoireRequest(finalRepertoire.id, payload));
        onClose();
    };

    return (
        <KmsModal
            open={open}
            title={isEdit ? "Modifier le document" : "Ajouter document/média"}
            onClose={onClose}
            footer={
                <>
                    <button className="kms-btn" type="button" onClick={onClose}>Annuler</button>
                    <button className="kms-btn kms-btn--primary" type="button" onClick={handleSubmit} disabled={saveLoading || (!isEdit && !canCreateOnFinalRepertoire)}>
                        <Upload size={15} />
                        Enregistrer
                    </button>
                </>
            }
        >
            <div className="kms-form-grid">
                {initialRepertoire && !isEdit && !allowRepertoireChange ? (
                    <div className="kms-field kms-field--full">
                        <label>Répertoire</label>
                        <div className="kms-readonly-path">
                            <strong>{initialRepertoire.__path || initialRepertoire.path || initialRepertoire.name || optionLabel(initialRepertoire)}</strong>
                            <button className="kms-btn" type="button" onClick={() => setAllowRepertoireChange(true)}>Changer répertoire</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <SelectField
                            label="Répertoire parent"
                            value={parentRepertoireId}
                            onChange={(value) => {
                                setParentRepertoireId(value);
                                setSubRepertoireId("");
                            }}
                        >
                            <option value="">Sélectionner</option>
                            {parentOptions.map((item) => (
                                <option key={item.id} value={item.id}>{item.__path || optionLabel(item)}</option>
                            ))}
                        </SelectField>
                        <SelectField label="Sous-répertoire" value={subRepertoireId} onChange={setSubRepertoireId}>
                            <option value="">Aucun</option>
                            {subOptions.map((item) => (
                                <option key={item.id} value={item.id}>{optionLabel(item)}</option>
                            ))}
                        </SelectField>
                    </>
                )}
                {!canCreateOnFinalRepertoire ? (
                    <div className="kms-form-message kms-field--full">Création non autorisée sur ce répertoire</div>
                ) : null}
                <TextField label="Titre" value={form.title} onChange={(value) => patch("title", value)} />
                <SelectField label="Type fichier" value={form.documentType} onChange={(value) => patch("documentType", value)}>
                    {DOCUMENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </SelectField>
                <TextField label="Description" value={form.description} onChange={(value) => patch("description", value)} full textarea />
                <SelectField label="Contenu lié" value={form.contentId} onChange={(value) => patch("contentId", value)}>
                    <option value="">Document indépendant</option>
                    {(contents || []).map((content) => <option key={content.id} value={content.id}>{getContentTitle(content)}</option>)}
                </SelectField>
                <SelectField label="Catégorie" value={form.categoryId} onChange={(value) => patch("categoryId", value)}>
                    <option value="">Aucune</option>
                    {(categories || []).map((category) => <option key={category.id} value={category.id}>{optionLabel(category)}</option>)}
                </SelectField>
                <TagsSelect tags={tags} value={form.tagIds} onChange={(value) => patch("tagIds", value)} />
                <div className="kms-field kms-field--full">
                    <label>Fichier</label>
                    <input type="file" onChange={(event) => patch("file", event.target.files?.[0] || null)} />
                </div>
            </div>
        </KmsModal>
    );
}

function DocumentDetailModal({ open, onClose, document }) {
    if (!document) return null;
    return (
        <KmsModal open={open} title="Détail document" onClose={onClose}>
            <div className="kms-detail-grid">
                <div className="kms-detail-item"><span>Type</span><strong>{documentTypeLabel(document.documentType)}</strong></div>
                <div className="kms-detail-item"><span>Répertoire</span><strong>{document.repertoireName || "-"}</strong></div>
                <div className="kms-detail-item"><span>Contenu lié</span><strong>{document.contentTitle || "-"}</strong></div>
                <div className="kms-detail-item"><span>Taille</span><strong>{formatBytes(document.sizeInBytes)}</strong></div>
                <div className="kms-detail-item"><span>Auteur</span><strong>{document.uploadedByFullName || "-"}</strong></div>
                <div className="kms-detail-item"><span>Catégorie</span><strong>{optionLabel(document.category)}</strong></div>
            </div>
            <div className="kms-preview">
                {document.previewable ? (
                    <button className="kms-btn" type="button" onClick={() => openDocumentUrl(document, "preview")}>
                        <Eye size={15} />
                        Preview
                    </button>
                ) : (
                    "Preview non disponible"
                )}
                <button className="kms-btn" type="button" onClick={() => openDocumentUrl(document, "download")} style={{ marginLeft: 8 }}>
                    <Download size={15} />
                    Télécharger
                </button>
            </div>
        </KmsModal>
    );
}

function KmsCommentsPanel({ content }) {
    const dispatch = useDispatch();
    const { commentsByContent, commentSaveLoadingByContent } = useSelector((state) => state.kmsContent || {});
    const [comment, setComment] = useState("");
    const comments = commentsByContent?.[content?.id] || [];

    const submitComment = () => {
        if (!content?.id || !comment.trim()) return;
        dispatch(createKmsContentCommentRequest(content.id, comment.trim()));
        setComment("");
    };

    return (
        <section className="kms-detail-section">
            <div className="kms-section-title"><h2>Commentaires</h2><span>{comments.length}</span></div>
            <div className="kms-chat-list">
                {comments.length ? comments.map((item) => (
                    <div className="kms-chat-item" key={item.id || `${item.createdAt}-${item.createdByFullName}`}>
                        <div className="kms-chat-avatar">{String(getCommentAuthorName(item)).slice(0, 1).toUpperCase()}</div>
                        <div className="kms-chat-bubble">
                            <div className="kms-chat-meta">
                                <strong>{getCommentAuthorName(item)}</strong>
                                <span>{formatDate(item.createdAt)}</span>
                            </div>
                            <div>{item.message || item.comment || ""}</div>
                        </div>
                    </div>
                )) : <KmsEmpty>Aucun commentaire</KmsEmpty>}
            </div>
            <div className="kms-chat-compose">
                <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Ajouter un commentaire..." />
                <button className="kms-btn kms-btn--primary" type="button" onClick={submitComment} disabled={!comment.trim() || commentSaveLoadingByContent?.[content?.id]}>
                    Commenter
                </button>
            </div>
        </section>
    );
}

function KmsCollaboratorEngagement({ content }) {
    const dispatch = useDispatch();
    const {
        subscriptionByContent,
        ratingSummaryByContent,
        subscriptionSaveLoadingByContent,
        ratingSaveLoadingByContent,
        subscriptionErrorByContent,
        ratingErrorByContent,
    } = useSelector((state) => state.kmsContent || {});
    const subscription = subscriptionByContent?.[content?.id];
    const ratingSummary = ratingSummaryByContent?.[content?.id];
    const subscriptionSaving = Boolean(subscriptionSaveLoadingByContent?.[content?.id]);

    return (
        <section className="kms-detail-section">
            <div className="kms-section-title"><h2>Collaboration</h2></div>
            <div className="kms-engagement-row">
                <button
                    className="kms-btn"
                    type="button"
                    disabled={!content?.id || subscriptionSaving || (!isSubscribed(subscription) && subscription?.canSubscribe === false)}
                    onClick={() => dispatch(isSubscribed(subscription) ? unsubscribeKmsContentRequest(content.id) : subscribeKmsContentRequest(content.id))}
                >
                    {subscriptionSaving ? "Traitement..." : isSubscribed(subscription) ? "Se désabonner" : "S'abonner"}
                </button>
                <span>{isSubscribed(subscription) ? "Abonné" : "Non abonné"}{subscription?.subscriberCount !== undefined ? ` - ${subscription.subscriberCount} abonné(s)` : ""}</span>
                <div className="kms-rating kms-rating--inline">
                    <span>Évaluation</span>
                    <span>{formatRatingSummary(ratingSummary)}</span>
                    {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                            key={rating}
                            className={`kms-star ${getRatingValue(ratingSummary) >= rating ? "is-active" : ""}`}
                            type="button"
                            disabled={!content?.id || ratingSaveLoadingByContent?.[content?.id] || ratingSummary?.canRate === false}
                            onClick={() => dispatch(upsertKmsContentRatingRequest(content.id, rating))}
                        >
                            <Star size={14} />
                        </button>
                    ))}
                </div>
                {subscriptionErrorByContent?.[content?.id] ? <span className="kms-inline-error">{subscriptionErrorByContent[content.id]}</span> : null}
                {ratingErrorByContent?.[content?.id] ? <span className="kms-inline-error">{ratingErrorByContent[content.id]}</span> : null}
            </div>
        </section>
    );
}

function KmsDocumentCommentsPanel({ document }) {
    const dispatch = useDispatch();
    const { commentsByDocument, commentSaveLoadingByDocument } = useSelector((state) => state.kmsDocument || {});
    const [comment, setComment] = useState("");
    const comments = commentsByDocument?.[document?.id] || [];

    const submitComment = () => {
        if (!document?.id || !comment.trim()) return;
        dispatch(createKmsDocumentCommentRequest(document.id, comment.trim()));
        setComment("");
    };

    return (
        <section className="kms-detail-section">
            <div className="kms-section-title"><h2>Commentaires</h2><span>{comments.length}</span></div>
            <div className="kms-chat-list">
                {comments.length ? comments.map((item) => (
                    <div className="kms-chat-item" key={item.id || `${item.createdAt}-${getCommentAuthorName(item)}`}>
                        <div className="kms-chat-avatar">{String(getCommentAuthorName(item)).slice(0, 1).toUpperCase()}</div>
                        <div className="kms-chat-bubble">
                            <div className="kms-chat-meta">
                                <strong>{getCommentAuthorName(item)}</strong>
                                <span>{formatDate(item.createdAt)}</span>
                            </div>
                            <div>{item.message || item.comment || ""}</div>
                        </div>
                    </div>
                )) : <KmsEmpty>Aucun commentaire</KmsEmpty>}
            </div>
            <div className="kms-chat-compose">
                <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Ajouter un commentaire..." />
                <button className="kms-btn kms-btn--primary" type="button" onClick={submitComment} disabled={!comment.trim() || commentSaveLoadingByDocument?.[document?.id]}>
                    Commenter
                </button>
            </div>
        </section>
    );
}

function KmsDocumentEngagement({ document }) {
    const dispatch = useDispatch();
    const {
        subscriptionByDocument,
        ratingSummaryByDocument,
        subscriptionSaveLoadingByDocument,
        ratingSaveLoadingByDocument,
        subscriptionErrorByDocument,
        ratingErrorByDocument,
    } = useSelector((state) => state.kmsDocument || {});
    const subscription = subscriptionByDocument?.[document?.id];
    const ratingSummary = ratingSummaryByDocument?.[document?.id];

    return (
        <section className="kms-detail-section">
            <div className="kms-section-title"><h2>Collaboration</h2></div>
            <div className="kms-engagement-row">
                <button
                    className="kms-btn"
                    type="button"
                    disabled={!document?.id || subscriptionSaveLoadingByDocument?.[document?.id]}
                    onClick={() => dispatch(isSubscribed(subscription) ? unsubscribeKmsDocumentRequest(document.id) : subscribeKmsDocumentRequest(document.id))}
                >
                    {subscriptionSaveLoadingByDocument?.[document?.id] ? "Traitement..." : isSubscribed(subscription) ? "Se désabonner" : "S'abonner"}
                </button>
                <span>{isSubscribed(subscription) ? "Abonné" : "Non abonné"}{subscription?.subscriberCount !== undefined ? ` - ${subscription.subscriberCount} abonné(s)` : ""}</span>
                <div className="kms-rating kms-rating--inline">
                    <span>Évaluation</span>
                    <span>{formatRatingSummary(ratingSummary)}</span>
                    {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                            key={rating}
                            className={`kms-star ${getRatingValue(ratingSummary) >= rating ? "is-active" : ""}`}
                            type="button"
                            disabled={!document?.id || ratingSaveLoadingByDocument?.[document?.id] || ratingSummary?.canRate === false}
                            onClick={() => dispatch(upsertKmsDocumentRatingRequest(document.id, rating))}
                        >
                            <Star size={14} />
                        </button>
                    ))}
                </div>
                {subscriptionErrorByDocument?.[document?.id] ? <span className="kms-inline-error">{subscriptionErrorByDocument[document.id]}</span> : null}
                {ratingErrorByDocument?.[document?.id] ? <span className="kms-inline-error">{ratingErrorByDocument[document.id]}</span> : null}
            </div>
        </section>
    );
}

function KmsWorkflowMeta({ content }) {
    const { workflowHistoryByContent } = useSelector((state) => state.kmsContent || {});
    const workflow = workflowHistoryByContent?.[content?.id] || [];

    return (
        <section className="kms-detail-section kms-detail-section--secondary">
            <div className="kms-section-title"><h2>Workflow</h2></div>
            {workflow.length ? (
                <div className="kms-workflow-list">
                    {workflow.map((item) => (
                        <div className="kms-workflow-item" key={item.id || `${item.action}-${item.createdAt}`}>
                            <strong>{statusLabel(item.oldStatus)} â†’ {statusLabel(item.newStatus)}</strong>
                            <span>{item.comment || item.action || "-"}</span>
                        </div>
                    ))}
                </div>
            ) : <KmsEmpty>Aucun historique</KmsEmpty>}
        </section>
    );
}

function KmsCorrectionCallout({ meta, onModify, onResubmit, resubmitDisabled = false }) {
    if (!meta) return null;
    return (
        <section className="kms-correction-callout">
            <div className="kms-correction-callout__header">
                <span className="kms-correction-pill"><AlertCircle size={14} />À corriger</span>
            </div>
            <div className="kms-correction-callout__body">
                <p><strong>Motif :</strong> {meta.comment || "Aucun motif renseigné."}</p>
                <p><strong>Par :</strong> {meta.actor || "Administrateur de connaissance"}</p>
                <p><strong>Le :</strong> {formatDate(meta.date)}</p>
            </div>
            <div className="kms-correction-callout__actions">
                <button className="kms-btn kms-btn--warning" type="button" onClick={onModify}>Modifier</button>
                <button className="kms-btn kms-btn--primary" type="button" onClick={onResubmit} disabled={resubmitDisabled}>Resoumettre</button>
            </div>
        </section>
    );
}

export function KmsContentDetailsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { contentId } = useParams();
    const { isAdmin, isCollaborator } = useKmsRole();
    const { currentContent, detailLoading, workflowHistoryByContent } = useSelector((state) => state.kmsContent || {});
    const linkedDocuments = useSelector((state) => state.kmsDocument?.byContent?.[contentId] || []);
    const content = currentContent && String(currentContent.id) === String(contentId) ? currentContent : null;
    const searchParams = new URLSearchParams(location.search);
    const isReviewMode = searchParams.get("viewMode") === "review" || searchParams.get("from") === "workflow";
    const [decision, setDecision] = useState(null);

    useEffect(() => {
        if (!contentId) return;
        dispatch(getKmsContentRequest(contentId));
        dispatch(getKmsDocumentsByContentRequest(contentId));
        if (!isReviewMode) {
            dispatch(getKmsContentCommentsRequest(contentId));
            dispatch(getKmsContentWorkflowHistoryRequest(contentId));
            if (isCollaborator) {
                dispatch(getKmsContentSubscriptionRequest(contentId));
                dispatch(getKmsContentRatingSummaryRequest(contentId));
            }
        }
    }, [contentId, dispatch, isCollaborator, isReviewMode]);

    if (detailLoading && !content) {
        return <div className="kms-page"><KmsPageHeader title="Détail contenu" /><KmsEmpty>Chargement...</KmsEmpty></div>;
    }

    if (!content) {
        return <div className="kms-page"><KmsPageHeader title="Détail contenu" /><KmsEmpty>Contenu introuvable</KmsEmpty></div>;
    }

    const body = content.type === "FAQ" ? `${content.faqQuestion || ""}\n\n${content.faqAnswer || ""}` : content.body || content.summary || "-";
    const looksHtml = /<\/?[a-z][\s\S]*>/i.test(body);
    const canReviewSubmitted = isReviewMode && String(content.status || "").toUpperCase() === "SUBMITTED" && (isAdmin || hasItemRight(content, "canValidate"));
    const correctionMeta = !isReviewMode ? getContentCorrectionMeta(content, workflowHistoryByContent) : null;
    const confirmReviewDecision = (comment) => {
        if (!decision) return;
        if (decision.action === "approve") dispatch(approveKmsContentRequest(content.id, comment));
        if (decision.action === "reject") dispatch(rejectKmsContentRequest(content.id, comment));
        if (decision.action === "return") dispatch(returnKmsContentToDraftRequest(content.id, comment));
        setDecision(null);
    };

    return (
        <div className="kms-page">
            <KmsPageHeader
                title={getContentTitle(content)}
                actions={
                    <>
                        <button className="kms-btn" type="button" onClick={() => navigate(isReviewMode ? "/kms/contenus/traitement" : "/kms/contenus")}>Retour</button>
                        {canReviewSubmitted && <button className="kms-icon-btn kms-icon-btn--success" type="button" title="Valider" onClick={() => setDecision({ action: "approve", title: "Valider la publication" })}><Check size={16} /></button>}
                        {canReviewSubmitted && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Rejeter" onClick={() => setDecision({ action: "reject", title: "Rejeter la publication" })}><X size={16} /></button>}
                        {canReviewSubmitted && <button className="kms-icon-btn kms-icon-btn--warning" type="button" title="Retour correction" onClick={() => setDecision({ action: "return", title: "Retour correction" })}><RotateCcw size={16} /></button>}
                        {!isReviewMode && (isAdmin || hasItemRight(content, "canArchive")) && <button className="kms-btn" type="button" onClick={() => dispatch(archiveKmsContentRequest(content.id, "Archivage", content.repertoireId))}><Archive size={15} />Archiver</button>}
                        {!isReviewMode && (isAdmin || hasItemRight(content, "canArchive")) && <button className="kms-btn kms-btn--danger" type="button" onClick={() => confirmMoveContentToTrash(dispatch, content)}><Trash2 size={15} />Corbeille</button>}
                    </>
                }
            />
            <main className="kms-detail-page">
                <section className="kms-detail-hero">
                    <div className="kms-detail-meta-row">
                        <KmsTypeBadge>{contentTypeLabel(content.type)}</KmsTypeBadge>
                        <KmsStatusBadge status={content.status} />
                        <span>{content.repertoireName || "-"}</span>
                        <span>{content.createdByFullName || "-"}</span>
                        <span>{formatDate(content.updatedAt || content.createdAt)}</span>
                    </div>
                </section>
                {!isReviewMode ? (
                    <KmsCorrectionCallout
                        meta={correctionMeta}
                        onModify={() => navigate("/kms/contenus")}
                        onResubmit={() => dispatch(submitKmsContentRequest(content.id, "Resoumission après correction"))}
                        resubmitDisabled={!hasItemRight(content, "canSubmit")}
                    />
                ) : null}

                <div className="kms-detail-layout">
                    <article className="kms-reader">
                        {looksHtml ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <div>{body}</div>}
                    </article>
                    <aside className="kms-detail-side">
                        <section className="kms-detail-section">
                            <div className="kms-section-title"><h2>Documents liés</h2><span>{linkedDocuments.length}</span></div>
                            {linkedDocuments.length ? linkedDocuments.map((doc) => (
                                <div className="kms-linked-doc" key={doc.id}>
                                    <div>
                                        <strong>{getDocumentTitle(doc)}</strong>
                                        <span>{documentTypeLabel(doc.documentType)}</span>
                                    </div>
                                    <div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Preview" onClick={() => openDocumentUrl(doc, "preview")}><FileText size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(doc, "download")}><Download size={14} /></button>
                                    </div>
                                </div>
                            )) : <KmsEmpty>Aucun document lié</KmsEmpty>}
                        </section>
                        {!isReviewMode && isCollaborator ? <KmsCollaboratorEngagement content={content} /> : null}
                        {!isReviewMode ? <KmsCommentsPanel content={content} /> : null}
                        {/*{!isReviewMode ? <KmsWorkflowMeta content={content} /> : null}*/}
                    </aside>
                </div>
            </main>
            <WorkflowDecisionModal open={Boolean(decision)} title={decision?.title} onClose={() => setDecision(null)} onConfirm={confirmReviewDecision} />
        </div>
    );
}

class KmsDocumentPreview extends React.Component {
    state = { blobUrl: "", loading: false, error: "" };

    componentDidMount() {
        this.loadPreview();
    }

    componentDidUpdate(prevProps) {
        if (getPreviewUrl(prevProps.document) !== getPreviewUrl(this.props.document)) {
            this.revokeBlobUrl();
            this.loadPreview();
        }
    }

    componentWillUnmount() {
        this.revokeBlobUrl();
    }

    revokeBlobUrl() {
        if (this.state.blobUrl) URL.revokeObjectURL(this.state.blobUrl);
    }

    loadPreview() {
        const url = toDocumentApiUrl(getPreviewUrl(this.props.document));
        if (!url) {
            this.setState({ blobUrl: "", loading: false, error: "" });
            return;
        }
        this.setState({ blobUrl: "", loading: true, error: "" });
        fetch(url, { headers: getKmsFileHeaders() })
            .then((response) => {
                if (!response.ok) throw new Error("Preview non disponible");
                return response.blob();
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                this.setState({ blobUrl, loading: false, error: "" });
            })
            .catch(() => this.setState({ blobUrl: "", loading: false, error: "Preview non disponible" }));
    }

    render() {
        const { document } = this.props;
        const { blobUrl, loading, error } = this.state;
        const type = String(document?.documentType || "").toUpperCase();
        const url = toDocumentApiUrl(getPreviewUrl(document));

        if (!url) return <KmsEmpty>Preview non disponible</KmsEmpty>;
        if (loading) return <KmsEmpty>Chargement preview</KmsEmpty>;
        if (error || !blobUrl) return <KmsEmpty>{error || "Preview non disponible"}</KmsEmpty>;
        if (type === "IMAGE") return <img className="kms-document-preview-media" src={blobUrl} alt={getDocumentTitle(document)} />;
        if (type === "VIDEO") return <video className="kms-document-preview-media" src={blobUrl} controls />;
        if (type && type !== "PDF") {
            return (
                <div className="kms-preview-fallback">
                    <FileText size={38} />
                    <strong>{getDocumentTitle(document)}</strong>
                    <span>{documentTypeLabel(type)}</span>
                    <button className="kms-btn kms-btn--primary" type="button" onClick={() => openDocumentUrl(document, "download")}>
                        <Download size={15} />Télécharger
                    </button>
                </div>
            );
        }
        return <iframe className="kms-document-preview-frame" title={getDocumentTitle(document)} src={blobUrl} />;
    }
}

function KmsCollaboratorTopbar({ q, onQuery, right }) {
    return (
        <div className="kms-collab-topbar">
            <button className="kms-collab-filter" type="button">FILTRER ET TRIER</button>
            <div className="kms-collab-search"><Search size={16} /><input value={q} onChange={(event) => onQuery(event.target.value)} placeholder="Rechercher" /></div>
            {right}
        </div>
    );
}

function KmsCollaboratorFilterBar({ children }) {
    return <div className="kms-collab-filters">{children}</div>;
}

function KmsDirectoryCards({ tree, onOpen }) {
    const rows = flattenTree(tree || []).filter((item) => Number(item.__level || 0) === 0);
    return (
        <section className="kms-collab-section">
            <h2>Répertoires</h2>
            <div className="kms-collab-grid kms-collab-grid--folders">
                {rows.length ? rows.map((item) => (
                    <button className="kms-folder-card" type="button" key={item.id} onClick={() => onOpen(item)}>
                        <span className="kms-collab-check" />
                        <Folder size={18} />
                        <strong>{item.__label || optionLabel(item)}</strong>
                        <MoreVertical size={17} />
                    </button>
                )) : <KmsEmpty>Aucun répertoire</KmsEmpty>}
            </div>
        </section>
    );
}

function KmsContentCards({ rows }) {
    const navigate = useNavigate();
    return (
        <section className="kms-collab-section">
            <h2>Contenu Web</h2>
            <div className="kms-collab-grid">
                {rows.length ? rows.map((item) => (
                    <article className="kms-library-card" key={item.id}>
                        <div className="kms-library-card-top">
                            <span className="kms-collab-check" />
                            <FileText size={48} />
                            <button className="kms-library-menu" type="button" aria-label="Actions"><MoreVertical size={17} /></button>
                        </div>
                        <div className="kms-library-card-body">
                            <h3>{getContentTitle(item)}</h3>
                            <span>{contentTypeLabel(item.type)} Â· {item.repertoireName || "-"}</span>
                            <span>{optionLabel(item.category)} Â· {formatDate(item.updatedAt)}</span>
                            <KmsStatusBadge status={item.status} />
                        </div>
                        <button className="kms-library-primary" type="button" onClick={() => navigate(`/kms/collaborateur/contenus/${item.id}`)}>
                            Afficher le contenu
                        </button>
                    </article>
                )) : <KmsEmpty>Aucun contenu</KmsEmpty>}
            </div>
        </section>
    );
}

function KmsDocumentCards({ rows }) {
    const navigate = useNavigate();
    return (
        <section className="kms-collab-section">
            <h2>Documents & Médias</h2>
            <div className="kms-collab-grid">
                {rows.length ? rows.map((item) => (
                    <article className="kms-library-card" key={item.id}>
                        <div className="kms-library-card-top">
                            <span className="kms-collab-check" />
                            {documentIcon(item.documentType)}
                            <button className="kms-library-menu" type="button" aria-label="Actions"><MoreVertical size={17} /></button>
                        </div>
                        <div className="kms-library-card-body">
                            <h3>{getDocumentTitle(item)}</h3>
                            <span>{documentTypeLabel(item.documentType)} Â· {formatDate(item.updatedAt)}</span>
                        </div>
                        <div className="kms-library-actions">
                            <button type="button" onClick={() => navigate(`/kms/collaborateur/documents/${item.id}`)}>Prévisualiser</button>
                            <button type="button" onClick={() => openDocumentUrl(item, "download")}>Télécharger</button>
                        </div>
                    </article>
                )) : <KmsEmpty>Aucun document</KmsEmpty>}
            </div>
        </section>
    );
}

const getCurrentDirectories = (tree, currentDirectory, query) => {
    const value = normalizeText(query);
    const nodes = currentDirectory ? nodeChildren(currentDirectory) : tree || [];
    if (!value) return nodes;
    return nodes.filter((node) => normalizeText(`${node.name || ""} ${node.nom || ""} ${node.code || ""} ${node.__path || ""}`).includes(value));
};

const hasChildDirectories = (directory) => nodeChildren(directory).length > 0;
const shouldShowLeafItems = (directory) => Boolean(directory) && !hasChildDirectories(directory);
const isLeafDirectoryInTree = (tree, directory) => Boolean(directory) && getCurrentDirectories(tree, directory, "").length === 0;

const getBreadcrumb = (tree, currentDirectory) => {
    if (!currentDirectory) return [];
    const findPath = (nodes = [], targetId, path = []) => {
        for (const node of nodes) {
            const next = [...path, node];
            if (String(node.id) === String(targetId)) return next;
            const found = findPath(nodeChildren(node), targetId, next);
            if (found.length) return found;
        }
        return [];
    };
    return findPath(tree || [], currentDirectory.id);
};

const getLatestReturnForCorrection = (workflow = []) =>
    (workflow || []).find(
        (item) =>
            String(item?.newStatus || "").toUpperCase() === "DRAFT" &&
            String(item?.action || "").toUpperCase().includes("RETURN")
    ) || null;

const getLatestRejection = (workflow = []) =>
    (workflow || []).find(
        (item) =>
            String(item?.newStatus || "").toUpperCase() === "REJECTED" ||
            String(item?.action || "").toUpperCase().includes("REJECT")
    ) || null;

const isReturnedForCorrectionStatus = (value) => String(value || "").toUpperCase() === "RETURNED_FOR_CORRECTION";

const isContentAwaitingCorrection = (item, workflowByContent = {}) =>
    isReturnedForCorrectionStatus(item?.status) ||
    (String(item?.status || "").toUpperCase() === "DRAFT" && Boolean(getLatestReturnForCorrection(workflowByContent?.[item?.id] || [])));

const isDocumentAwaitingCorrection = (item) =>
    isReturnedForCorrectionStatus(item?.status) ||
    (String(item?.status || "").toUpperCase() === "DRAFT" && Boolean(item?.returnedForCorrectionAt || item?.workflowComment));

const getContentCorrectionMeta = (content, workflowByContent = {}) => {
    if (isReturnedForCorrectionStatus(content?.status) && !workflowByContent?.[content?.id]?.length) {
        return {
            comment: "",
            actor: "Administrateur de connaissance",
            date: content?.updatedAt || content?.createdAt,
        };
    }
    const latest = getLatestReturnForCorrection(workflowByContent?.[content?.id] || []);
    if (!latest) return null;
    return {
        comment: latest.comment || "",
        actor: latest.actedByFullName || "Administrateur de connaissance",
        date: latest.createdAt,
    };
};

const getContentRejectionMeta = (content, workflowByContent = {}) => {
    const latest = getLatestRejection(workflowByContent?.[content?.id] || []);
    if (!latest) return null;
    return {
        comment: latest.comment || "",
        actor: latest.actedByFullName || "Administrateur de connaissance",
        date: latest.createdAt,
    };
};

const getDocumentCorrectionMeta = (kmsDocument) => {
    if (!isDocumentAwaitingCorrection(kmsDocument)) return null;
    return {
        comment: kmsDocument.workflowComment || "",
        actor: "Administrateur de connaissance",
        date: kmsDocument.returnedForCorrectionAt || kmsDocument.updatedAt,
    };
};

const getDocumentRejectionMeta = (kmsDocument) => {
    if (String(kmsDocument?.status || "").toUpperCase() !== "REJECTED") return null;
    return {
        comment: kmsDocument.workflowComment || "",
        actor: "Administrateur de connaissance",
        date: kmsDocument.rejectedAt || kmsDocument.updatedAt,
    };
};

const getShortWorkflowComment = (comment) => {
    const value = String(comment || "").trim();
    if (!value) return "";
    return value.length > 90 ? `${value.slice(0, 87)}...` : value;
};

function KmsCollabBreadcrumb({ tree, currentDirectory, onRoot, onSelect }) {
    const path = getBreadcrumb(tree, currentDirectory);
    return (
        <nav className="kms-collab-breadcrumb">
            <button type="button" onClick={onRoot}>Bibliothèque KMS</button>
            {path.map((item) => (
                <React.Fragment key={item.id}>
                    <span>/</span>
                    <button type="button" onClick={() => onSelect(item)}>{item.name || item.nom || item.code || `Répertoire ${item.id}`}</button>
                </React.Fragment>
            ))}
        </nav>
    );
}

function KmsCollabDirectoryCards({ rows, onOpen, contents = [], documents = [], kind = "content" }) {
    const counts = useMemo(() => {
        const map = new Map();
        (kind === "documents" ? documents : contents).forEach((item) => {
            const key = String(itemRepertoireId(item) || "");
            map.set(key, (map.get(key) || 0) + 1);
        });
        return map;
    }, [contents, documents, kind]);

    return (
        <section className="kms-collab-section">
            <h2>Répertoires</h2>
            <div className="kms-collab-grid kms-collab-grid--folders">
                {rows.length ? rows.map((item) => (
                    <button className="kms-folder-card" type="button" key={item.id} onClick={() => onOpen(item)}>
                        <Folder size={18} />
                        <div>
                            <strong>{item.__label || item.name || item.nom || optionLabel(item)}</strong>
                            {/*<span>{nodeChildren(item).length} sous-répertoire(s) Â· {counts.get(String(item.id)) || 0} élément(s)</span>*/}
                        </div>
                    </button>
                )) : <KmsEmpty>Aucun répertoire</KmsEmpty>}
            </div>
        </section>
    );
}

function KmsDirectoryBrowser({ tree, currentDirectory, onRoot, onSelect, query, contents = [], documents = [], kind = "content" }) {
    return (
        <>
            <KmsCollabBreadcrumb tree={tree} currentDirectory={currentDirectory} onRoot={onRoot} onSelect={onSelect} />
            <KmsCollabDirectoryCards rows={getCurrentDirectories(tree, currentDirectory, query)} onOpen={onSelect} contents={contents} documents={documents} kind={kind} />
            {currentDirectory ? (
                <div className="kms-collab-current">Répertoire : <strong>{currentDirectory.__path || currentDirectory.name || optionLabel(currentDirectory)}</strong></div>
            ) : null}
        </>
    );
}

function KmsCollabContentTable({ rows }) {
    const navigate = useNavigate();
    return (
        <section className="kms-collab-section">
            <h2>Contenus web</h2>
            <div className="kms-table-wrap">
                <table className="kms-table">
                    <thead><tr><th>Titre</th><th>Type</th><th>Catégorie / Tags</th><th>Statut</th><th>Date</th><th className="is-right">Action</th></tr></thead>
                    <tbody>{rows.length ? rows.map((item) => (
                        <tr key={item.id}>
                            <td><div className="kms-row-title">{getContentTitle(item)}</div><div className="kms-row-sub">{item.repertoireName || "-"}</div></td>
                            <td><KmsTypeBadge>{contentTypeLabel(item.type)}</KmsTypeBadge></td>
                            <td>{optionLabel(item.category)}{(item.tags || []).length ? ` / ${(item.tags || []).map(optionLabel).join(", ")}` : ""}</td>
                            <td><KmsStatusBadge status={item.status} /></td>
                            <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                            <td className="is-right"><button className="kms-icon-btn" type="button" title="Afficher le contenu" onClick={() => navigate(`/kms/collaborateur/contenus/${item.id}`)}><Eye size={14} /></button></td>
                        </tr>
                    )) : <tr><td colSpan={6}><KmsEmpty>Aucun contenu</KmsEmpty></td></tr>}</tbody>
                </table>
            </div>
        </section>
    );
}

function KmsCollabDocumentTable({ rows }) {
    const navigate = useNavigate();
    return (
        <section className="kms-collab-section">
            <h2>Documents & Médias</h2>
            <div className="kms-table-wrap">
                <table className="kms-table">
                    <thead><tr><th>Nom</th><th>Type fichier</th><th>Catégorie / Tags</th><th>Contenu lié</th><th>Date</th><th className="is-right">Actions</th></tr></thead>
                    <tbody>{rows.length ? rows.map((item) => (
                        <tr key={item.id}>
                            <td><div className="kms-row-title">{getDocumentTitle(item)}</div><div className="kms-row-sub">{item.repertoireName || "-"}</div></td>
                            <td><KmsTypeBadge>{documentTypeLabel(item.documentType)}</KmsTypeBadge></td>
                            <td>{optionLabel(item.category)}{(item.tags || []).length ? ` / ${(item.tags || []).map(optionLabel).join(", ")}` : ""}</td>
                            <td>{item.contentTitle || "-"}</td>
                            <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                            <td className="is-right"><div className="kms-row-actions">
                                <button className="kms-icon-btn" type="button" title="Preview" onClick={() => navigate(`/kms/collaborateur/documents/${item.id}`)}><Eye size={14} /></button>
                                <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(item, "download")}><Download size={14} /></button>
                            </div></td>
                        </tr>
                    )) : <tr><td colSpan={6}><KmsEmpty>Aucun document</KmsEmpty></td></tr>}</tbody>
                </table>
            </div>
        </section>
    );
}

export function KmsCollaboratorHomePage() {
    const { tree, contents, documents } = useKmsLibraryData();
    const [q, setQ] = useState("");
    const [selectedDirectory, setSelectedDirectory] = useState(null);
    const [activeTab, setActiveTab] = useState("contents");
    const contentRows = filterByText(
        selectedDirectory ? contents.filter((item) => String(itemRepertoireId(item)) === String(selectedDirectory.id)) : [],
        q,
        [getContentTitle, (item) => item.summary, (item) => item.repertoireName]
    );
    const documentRows = filterByText(
        selectedDirectory ? documents.filter((item) => String(itemRepertoireId(item)) === String(selectedDirectory.id)) : [],
        q,
        [getDocumentTitle, (item) => item.description, (item) => item.repertoireName]
    );

    return (
        <div className="kms-collab-page">
            {/*<KmsPageHeader title="Bibliothèque KMS" />*/}
            <KmsCollaboratorTopbar q={q} onQuery={setQ} />
            <KmsCollabBreadcrumb tree={tree} currentDirectory={selectedDirectory} onRoot={() => setSelectedDirectory(null)} onSelect={setSelectedDirectory} />
            <KmsCollabDirectoryCards rows={getCurrentDirectories(tree, selectedDirectory, q)} onOpen={setSelectedDirectory} contents={contents} documents={documents} />
            {selectedDirectory ? <div className="kms-collab-current">Répertoire : <strong>{selectedDirectory.__path || selectedDirectory.name}</strong></div> : null}
            {selectedDirectory ? (
                <>
                    <div className="kms-tabs">
                        <button className={`kms-tab ${activeTab === "contents" ? "is-active" : ""}`} type="button" onClick={() => setActiveTab("contents")}>
                            Contenus web <span className="kms-tab-count">{contentRows.length}</span>
                        </button>
                        <button className={`kms-tab ${activeTab === "documents" ? "is-active" : ""}`} type="button" onClick={() => setActiveTab("documents")}>
                            Documents & Médias <span className="kms-tab-count">{documentRows.length}</span>
                        </button>
                    </div>
                    {activeTab === "contents" ? <KmsCollabContentTable rows={contentRows} /> : <KmsCollabDocumentTable rows={documentRows} />}
                </>
            ) : null}
        </div>
    );
}

export function KmsCollaboratorContentPage() {
    const dispatch = useDispatch();
    const { contents, tree, contentState } = useKmsLibraryData();
    const { options } = useSelector((state) => state.kmsTaxonomy || {});
    const [q, setQ] = useState("");
    const [currentDirectory, setCurrentDirectory] = useState(null);
    const [filters, setFilters] = useState({ repertoireId: "", type: "", categoryId: "", tagId: "", status: "" });
    const repertoireOptions = useMemo(() => flattenTree(tree || []), [tree]);
    useEffect(() => { dispatch(getKmsTaxonomyOptionsRequest()); }, [dispatch]);
    const activeRepertoireId = currentDirectory?.id || filters.repertoireId;
    let rows = activeRepertoireId
        ? contents.filter((item) => String(itemRepertoireId(item) || "") === String(activeRepertoireId))
        : [];
    if (filters.type) rows = rows.filter((item) => item.type === filters.type);
    if (filters.categoryId) rows = rows.filter((item) => String(categoryIdOf(item)) === String(filters.categoryId));
    if (filters.tagId) rows = rows.filter((item) => hasTagId(item, filters.tagId));
    if (filters.status) rows = rows.filter((item) => item.status === filters.status);
    rows = filterByText(rows, q, [getContentTitle, (item) => item.summary, (item) => item.repertoireName, (item) => optionLabel(item.category)]);
    const patchFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    return (
        <div className="kms-collab-page">
            <KmsPageHeader title="Contenus web" />
            <KmsCollaboratorTopbar q={q} onQuery={setQ} />
            <KmsCollabBreadcrumb tree={tree} currentDirectory={currentDirectory} onRoot={() => { setCurrentDirectory(null); patchFilter("repertoireId", ""); }} onSelect={setCurrentDirectory} />
            <KmsCollaboratorFilterBar>
                <SelectField label="Répertoire" value={filters.repertoireId} onChange={(v) => { patchFilter("repertoireId", v); setCurrentDirectory(v ? getRepertoireNodeById(tree, v) : null); }}><option value="">Tous</option>{repertoireOptions.map((item) => <option key={item.id} value={item.id}>{item.__path || optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Type" value={filters.type} onChange={(v) => patchFilter("type", v)}><option value="">Tous</option>{CONTENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</SelectField>
                <SelectField label="Catégorie" value={filters.categoryId} onChange={(v) => patchFilter("categoryId", v)}><option value="">Toutes</option>{(options?.categories || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Tag" value={filters.tagId} onChange={(v) => patchFilter("tagId", v)}><option value="">Tous</option>{(options?.tags || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Statut" value={filters.status} onChange={(v) => patchFilter("status", v)}><option value="">Publié et approuvé</option>{["PUBLISHED","APPROVED"].map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}</SelectField>
            </KmsCollaboratorFilterBar>
            <KmsCollabDirectoryCards rows={getCurrentDirectories(tree, currentDirectory, q)} onOpen={setCurrentDirectory} contents={contents} />
            {currentDirectory ? (
                contentState.listLoading ? <KmsEmpty>Chargement...</KmsEmpty> : <KmsCollabContentTable rows={rows} />
            ) : null}
        </div>
    );
}

export function KmsCollaboratorDocumentPage() {
    const dispatch = useDispatch();
    const { documents, contents, tree, documentState } = useKmsLibraryData();
    const { options } = useSelector((state) => state.kmsTaxonomy || {});
    const [q, setQ] = useState("");
    const [currentDirectory, setCurrentDirectory] = useState(null);
    const [filters, setFilters] = useState({ repertoireId: "", documentType: "", categoryId: "", tagId: "", contentId: "" });
    const repertoireOptions = useMemo(() => flattenTree(tree || []), [tree]);
    useEffect(() => { dispatch(getKmsTaxonomyOptionsRequest()); }, [dispatch]);
    const activeRepertoireId = currentDirectory?.id || filters.repertoireId;
    let rows = activeRepertoireId
        ? documents.filter((item) => String(itemRepertoireId(item) || "") === String(activeRepertoireId))
        : [];
    if (filters.documentType) rows = rows.filter((item) => item.documentType === filters.documentType);
    if (filters.categoryId) rows = rows.filter((item) => String(categoryIdOf(item)) === String(filters.categoryId));
    if (filters.tagId) rows = rows.filter((item) => hasTagId(item, filters.tagId));
    if (filters.contentId) rows = rows.filter((item) => String(item.contentId || "") === String(filters.contentId));
    rows = filterByText(rows, q, [getDocumentTitle, (item) => item.description, (item) => item.repertoireName, (item) => item.contentTitle, (item) => optionLabel(item.category)]);
    const patchFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    return (
        <div className="kms-collab-page">
            <KmsPageHeader title="Documents & Médias" />
            <KmsCollaboratorTopbar q={q} onQuery={setQ} />
            <KmsCollabBreadcrumb tree={tree} currentDirectory={currentDirectory} onRoot={() => { setCurrentDirectory(null); patchFilter("repertoireId", ""); }} onSelect={setCurrentDirectory} />
            <KmsCollaboratorFilterBar>
                <SelectField label="Répertoire" value={filters.repertoireId} onChange={(v) => { patchFilter("repertoireId", v); setCurrentDirectory(v ? getRepertoireNodeById(tree, v) : null); }}><option value="">Tous</option>{repertoireOptions.map((item) => <option key={item.id} value={item.id}>{item.__path || optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Type fichier" value={filters.documentType} onChange={(v) => patchFilter("documentType", v)}><option value="">Tous</option>{DOCUMENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</SelectField>
                <SelectField label="Catégorie" value={filters.categoryId} onChange={(v) => patchFilter("categoryId", v)}><option value="">Toutes</option>{(options?.categories || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Tag" value={filters.tagId} onChange={(v) => patchFilter("tagId", v)}><option value="">Tous</option>{(options?.tags || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Contenu lié" value={filters.contentId} onChange={(v) => patchFilter("contentId", v)}><option value="">Tous</option>{contents.map((item) => <option key={item.id} value={item.id}>{getContentTitle(item)}</option>)}</SelectField>
            </KmsCollaboratorFilterBar>
            <KmsCollabDirectoryCards rows={getCurrentDirectories(tree, currentDirectory, q)} onOpen={setCurrentDirectory} documents={documents} kind="documents" />
            {currentDirectory ? (
                documentState.listLoading ? <KmsEmpty>Chargement...</KmsEmpty> : <KmsCollabDocumentTable rows={rows} />
            ) : null}
        </div>
    );
}

export function KmsSearchPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { items, loading, totalResults } = useSelector((state) => state.kmsSearch || {});
    const [keyword, setKeyword] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => dispatch(searchKmsRequest({ keyword })), 250);
        return () => clearTimeout(timer);
    }, [dispatch, keyword]);

    return (
        <div className="kms-page">
            <KmsPageHeader title="Recherche" />
            <div className="kms-toolbar">
                <div className="kms-search-box">
                    <Search size={14} />
                    <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="Recherche..." />
                </div>
            </div>
            <div className="kms-card">
                <div className="kms-card-header"><h2>Résultats</h2><span>{loading ? "..." : `${items?.length || totalResults || 0} résultat(s)`}</span></div>
                <div className="kms-table-wrap">
                    <table className="kms-table">
                        <thead><tr><th>Titre</th><th>Nature</th><th>Type</th><th>Répertoire</th><th>Statut</th><th className="is-right">Actions</th></tr></thead>
                        <tbody>{(items || []).length ? (items || []).map((item) => {
                            const isDoc = item.resultType === "DOCUMENT";
                            const normalized = isDoc ? normalizeDocumentResult(item) : normalizeContentResult(item);
                            return (
                                <tr key={`${item.resultType}-${item.id}`}>
                                    <td><div className="kms-row-title">{item.title || normalized.title}</div><div className="kms-row-sub">{item.snippet || "-"}</div></td>
                                    <td>{isDoc ? "Document/Média" : "Contenu web"}</td>
                                    <td><KmsTypeBadge>{isDoc ? documentTypeLabel(normalized.documentType) : contentTypeLabel(normalized.type)}</KmsTypeBadge></td>
                                    <td>{normalized.repertoireName || "-"}</td>
                                    <td>{isDoc ? <KmsStatusBadge status={normalized.status} /> : <KmsStatusBadge status={normalized.status} />}</td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Voir détail" onClick={() => navigate(isDoc ? `/kms/documents/${item.id}` : `/kms/contenus/${item.id}`)}><Eye size={14} /></button>
                                        {isDoc && <button className="kms-icon-btn" type="button" title="Preview" onClick={() => openDocumentUrl(normalized, "preview")}><FileText size={14} /></button>}
                                        {isDoc && <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(normalized, "download")}><Download size={14} /></button>}
                                    </div></td>
                                </tr>
                            );
                        }) : <tr><td colSpan={6}><KmsEmpty>Aucun résultat</KmsEmpty></td></tr>}</tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export function KmsCollaboratorSearchPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { items, loading, totalResults } = useSelector((state) => state.kmsSearch || {});
    const [keyword, setKeyword] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => dispatch(searchKmsRequest({ keyword })), 250);
        return () => clearTimeout(timer);
    }, [dispatch, keyword]);
    const visibleItems = (items || []).filter((item) =>
        item.resultType === "DOCUMENT"
            ? visibleForCollaborator(normalizeDocumentResult(item))
            : visibleForCollaborator({ status: item.contentStatus || item.status })
    );
    return (
        <div className="kms-collab-page">
            <KmsCollaboratorTopbar q={keyword} onQuery={setKeyword} />
            <section className="kms-collab-section">
                <h2>{loading ? "Recherche..." : `${visibleItems.length || totalResults || 0} résultat(s)`}</h2>
                <div className="kms-table-wrap">
                    <table className="kms-table">
                        <thead><tr><th>Titre</th><th>Nature</th><th>Type</th><th>Répertoire</th><th className="is-right">Actions</th></tr></thead>
                        <tbody>{visibleItems.length ? visibleItems.map((item) => {
                            const isDoc = item.resultType === "DOCUMENT";
                            return (
                                <tr key={`${item.resultType}-${item.id}`}>
                                    <td><div className="kms-row-title">{item.title}</div><div className="kms-row-sub">{item.snippet || "-"}</div></td>
                                    <td>{isDoc ? "Document/média" : "Contenu web"}</td>
                                    <td><KmsTypeBadge>{isDoc ? documentTypeLabel(item.documentType) : contentTypeLabel(item.contentType)}</KmsTypeBadge></td>
                                    <td>{item.repertoireName || "-"}</td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Afficher" onClick={() => navigate(isDoc ? `/kms/collaborateur/documents/${item.id}` : `/kms/collaborateur/contenus/${item.id}`)}><Eye size={14} /></button>
                                        {isDoc ? <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(item, "download")}><Download size={14} /></button> : null}
                                    </div></td>
                                </tr>
                            );
                        }) : <tr><td colSpan={5}><KmsEmpty>Aucun résultat</KmsEmpty></td></tr>}</tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

export function KmsCollaboratorContentDetailsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { contentId } = useParams();
    const { currentContent, detailLoading } = useSelector((state) => state.kmsContent || {});
    const linkedDocuments = useSelector((state) => state.kmsDocument?.byContent?.[contentId] || []);
    const content = currentContent && String(currentContent.id) === String(contentId) ? currentContent : null;

    useEffect(() => {
        if (!contentId) return;
        dispatch(getKmsContentRequest(contentId));
        dispatch(getKmsDocumentsByContentRequest(contentId));
        dispatch(getKmsContentCommentsRequest(contentId));
        dispatch(getKmsContentSubscriptionRequest(contentId));
        dispatch(getKmsContentRatingSummaryRequest(contentId));
    }, [contentId, dispatch]);

    if (detailLoading && !content) return <div className="kms-collab-page"><KmsEmpty>Chargement...</KmsEmpty></div>;
    if (!content) return <div className="kms-collab-page"><KmsEmpty>Contenu introuvable</KmsEmpty></div>;
    if (!visibleForCollaborator(content)) return <div className="kms-collab-page"><KmsEmpty>Contenu non disponible</KmsEmpty></div>;

    const body = content.type === "FAQ" ? `${content.faqQuestion || ""}\n\n${content.faqAnswer || ""}` : content.body || content.summary || "-";
    const looksHtml = /<\/?[a-z][\s\S]*>/i.test(body);

    return (
        <div className="kms-collab-page">
            <div className="kms-collab-detail-head">
                <div className="kms-collab-detail-head-left">
                    <button className="kms-btn" type="button" onClick={() => navigate("/kms/collaborateur/contenus")}><RotateCcw size={15} />Retour</button>
                    <h1>{getContentTitle(content)}</h1>
                    <KmsTypeBadge>{contentTypeLabel(content.type)}</KmsTypeBadge>
                    <KmsStatusBadge status={content.status} />
                    {content.repertoireName && <span className="kms-repertoire-badge"><Folder size={14} /> {content.repertoireName}</span>}
                </div>
            </div>

            <div className="kms-collab-layout">
                <aside className="kms-detail-side">
                    <section className="kms-detail-section">
                        <div className="kms-info-list">
                            <div><span>Auteur</span><strong>{content.createdByFullName || "-"}</strong></div>
                            <div><span>Créé le</span><strong>{formatDate(content.createdAt)}</strong></div>
                            <div><span>Modifié le</span><strong>{formatDate(content.updatedAt)}</strong></div>
                            <div><span>Catégories</span><strong>{optionLabel(content.category)}</strong></div>
                            <div><span>Tags</span><strong>{(content.tags || []).map(optionLabel).join(", ") || "-"}</strong></div>
                            <div><span>Répertoire</span><strong>{content.repertoireName || "-"}</strong></div>
                        </div>
                    </section>

                    <section className="kms-detail-section">
                        <div className="kms-section-title"><h2>Documents liés</h2><span>{linkedDocuments.length}</span></div>
                        {linkedDocuments.length ? linkedDocuments.map((doc) => (
                            <div className="kms-linked-doc" key={doc.id}>
                                <div><strong>{getDocumentTitle(doc)}</strong><span>{documentTypeLabel(doc.documentType)}</span></div>
                                <div className="kms-row-actions">
                                    <button className="kms-icon-btn" type="button" onClick={() => navigate(`/kms/collaborateur/documents/${doc.id}`)} title="Prévisualisation"><Eye size={14} /></button>
                                    <button className="kms-icon-btn" type="button" onClick={() => openDocumentUrl(doc, "download")} title="Téléchargement"><Download size={14} /></button>
                                </div>
                            </div>
                        )) : <KmsEmpty>Aucun document lié</KmsEmpty>}
                    </section>

                    <KmsCollaboratorEngagement content={content} />
                    <KmsCommentsPanel content={content} />
                </aside>

                <main>
                    <section className="kms-detail-section" style={{ margin: 0 }}>
                        <div className="kms-section-title"><h2>Détails</h2></div>
                        <article className="kms-collab-reader" style={{ margin: 0, border: 0, boxShadow: "none", maxWidth: "100%" }}>
                            {looksHtml ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <div style={{ whiteSpace: "pre-wrap" }}>{body}</div>}
                        </article>
                    </section>
                </main>
            </div>
        </div>
    );
}

export function KmsCollaboratorDocumentDetailsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { documentId } = useParams();
    const { currentDocument, detailLoading } = useSelector((state) => state.kmsDocument || {});
    const document = currentDocument && String(currentDocument.id) === String(documentId) ? currentDocument : null;

    useEffect(() => {
        if (!documentId) return;
        dispatch(getKmsDocumentRequest(documentId));
        dispatch(getKmsDocumentCommentsRequest(documentId));
        dispatch(getKmsDocumentSubscriptionRequest(documentId));
        dispatch(getKmsDocumentRatingSummaryRequest(documentId));
    }, [dispatch, documentId]);

    if (detailLoading && !document) return <div className="kms-collab-page"><KmsEmpty>Chargement...</KmsEmpty></div>;
    if (!document) return <div className="kms-collab-page"><KmsEmpty>Document introuvable</KmsEmpty></div>;

    return (
        <div className="kms-collab-page">
            <div className="kms-collab-detail-head">
                <div className="kms-collab-detail-head-left">
                    <button className="kms-btn" type="button" onClick={() => navigate("/kms/collaborateur/documents")}><RotateCcw size={15} />Retour</button>
                    <h1>{getDocumentTitle(document)}</h1>
                    <KmsTypeBadge>{documentTypeLabel(document.documentType)}</KmsTypeBadge>
                    <KmsStatusBadge status={document.status} />
                    {document.repertoireName && <span className="kms-repertoire-badge"><Folder size={14} /> {document.repertoireName}</span>}
                </div>
                <div className="kms-collab-detail-head-right">
                    <button className="kms-btn" type="button" onClick={() => openDocumentUrl(document, "preview")}><Eye size={15} /> Prévisualisation</button>
                    <button className="kms-btn kms-btn--primary" type="button" onClick={() => openDocumentUrl(document, "download")}><Download size={15} /> Téléchargement</button>
                </div>
            </div>

            <div className="kms-collab-layout">
                <aside className="kms-detail-side">
                    <section className="kms-detail-section">
                        <div className="kms-info-list">
                            <div><span>Auteur</span><strong>{document.uploadedByFullName || "-"}</strong></div>
                            <div><span>Créé le</span><strong>{formatDate(document.createdAt)}</strong></div>
                            <div><span>Modifié le</span><strong>{formatDate(document.updatedAt)}</strong></div>
                            <div><span>Catégories</span><strong>{optionLabel(document.category)}</strong></div>
                            <div><span>Tags</span><strong>{(document.tags || []).map(optionLabel).join(", ") || "-"}</strong></div>
                            <div><span>Répertoire</span><strong>{document.repertoireName || "-"}</strong></div>
                        </div>
                    </section>

                    <section className="kms-detail-section">
                        <div className="kms-section-title"><h2>Contenu lié</h2></div>
                        {document.contentId ? (
                            <div className="kms-linked-doc">
                                <div><strong>{document.contentTitle || "Contenu web"}</strong></div>
                                <div className="kms-row-actions">
                                    <button className="kms-icon-btn" type="button" onClick={() => navigate(`/kms/collaborateur/contenus/${document.contentId}`)} title="Détails"><Eye size={14} /></button>
                                </div>
                            </div>
                        ) : <KmsEmpty>Aucun contenu lié</KmsEmpty>}
                    </section>

                    <KmsDocumentEngagement document={document} />
                    <KmsDocumentCommentsPanel document={document} />
                </aside>

                <main>
                    <section className="kms-detail-section" style={{ margin: 0 }}>
                        <div className="kms-section-title"><h2>Prévisualisation</h2></div>
                        <div className="kms-document-preview kms-collab-doc-preview" style={{ margin: 0, border: 0, boxShadow: "none", maxWidth: "100%" }}>
                            <KmsDocumentPreview document={document} />
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
export function KmsDocumentDetailsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { documentId } = useParams();
    const { isAdmin, isCollaborator } = useKmsRole();
    const { currentDocument, detailLoading } = useSelector((state) => state.kmsDocument || {});
    const document = currentDocument && String(currentDocument.id) === String(documentId) ? currentDocument : null;
    const searchParams = new URLSearchParams(location.search);
    const isReviewMode = searchParams.get("viewMode") === "review" || searchParams.get("from") === "workflow";
    const [decision, setDecision] = useState(null);

    useEffect(() => {
        if (!documentId) return;
        dispatch(getKmsDocumentRequest(documentId));
        if (!isReviewMode) {
            dispatch(getKmsDocumentCommentsRequest(documentId));
            if (isCollaborator) {
                dispatch(getKmsDocumentSubscriptionRequest(documentId));
                dispatch(getKmsDocumentRatingSummaryRequest(documentId));
            }
        }
    }, [dispatch, documentId, isCollaborator, isReviewMode]);

    if (detailLoading && !document) {
        return <div className="kms-page"><KmsPageHeader title="Détail document" /><KmsEmpty>Chargement...</KmsEmpty></div>;
    }

    if (!document) {
        return <div className="kms-page"><KmsPageHeader title="Détail document" /><KmsEmpty>Document introuvable</KmsEmpty></div>;
    }

    const correctionMeta = !isReviewMode ? getDocumentCorrectionMeta(document) : null;

    return (
        <div className="kms-page">
            <KmsPageHeader
                title={getDocumentTitle(document)}
                actions={
                    <>
                        <button className="kms-btn" type="button" onClick={() => navigate(isReviewMode ? "/kms/contenus/traitement" : "/kms/documents-medias")}>Retour</button>
                        {isReviewMode && String(document.status || "").toUpperCase() === "SUBMITTED" && (isAdmin || hasItemRight(document, "canValidate")) && <button className="kms-icon-btn kms-icon-btn--success" type="button" title="Valider" onClick={() => setDecision({ action: "approve", title: "Valider la publication" })}><Check size={16} /></button>}
                        {isReviewMode && String(document.status || "").toUpperCase() === "SUBMITTED" && (isAdmin || hasItemRight(document, "canValidate")) && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Rejeter" onClick={() => setDecision({ action: "reject", title: "Rejeter la publication" })}><X size={16} /></button>}
                        {isReviewMode && String(document.status || "").toUpperCase() === "SUBMITTED" && (isAdmin || hasItemRight(document, "canValidate")) && <button className="kms-icon-btn kms-icon-btn--warning" type="button" title="Retour correction" onClick={() => setDecision({ action: "return", title: "Retour correction" })}><RotateCcw size={16} /></button>}
                        <button className="kms-btn" type="button" onClick={() => openDocumentUrl(document, "preview")}><FileText size={15} />Preview</button>
                        <button className="kms-btn kms-btn--primary" type="button" onClick={() => openDocumentUrl(document, "download")}><Download size={15} />Télécharger</button>
                        {!isReviewMode && isAdmin && <button className="kms-btn" type="button" onClick={() => Swal.fire({ icon: "info", text: "Modification des métadonnées depuis la liste.", confirmButtonText: "OK" })}><Edit size={15} />Modifier</button>}
                        {!isReviewMode && (isAdmin || hasItemRight(document, "canArchive")) && <button className="kms-btn kms-btn--danger" type="button" onClick={() => confirmMoveDocumentToTrash(dispatch, document)}><Trash2 size={15} />Corbeille</button>}
                    </>
                }
            />
            <main className="kms-detail-page">
                <section className="kms-detail-hero">
                    <div className="kms-detail-meta-row">
                        <KmsTypeBadge>{documentTypeLabel(document.documentType)}</KmsTypeBadge>
                        <span>{document.repertoireName || "-"}</span>
                        <span>{document.contentTitle || "Document indépendant"}</span>
                        <span>{document.uploadedByFullName || "-"}</span>
                        <span>{formatDate(document.updatedAt || document.createdAt)}</span>
                    </div>
                </section>
                {!isReviewMode ? (
                    <KmsCorrectionCallout
                        meta={correctionMeta}
                        onModify={() => navigate("/kms/documents-medias")}
                        onResubmit={() => dispatch(submitKmsDocumentRequest(document.id, "Resoumission après correction"))}
                        resubmitDisabled={!hasItemRight(document, "canSubmit")}
                    />
                ) : null}
                <div className="kms-document-layout">
                    <section className="kms-document-preview">
                        <KmsDocumentPreview document={document} />
                    </section>
                    <aside className="kms-detail-side">
                        <section className="kms-detail-section">
                            <div className="kms-section-title"><h2>Informations</h2></div>
                            <div className="kms-info-list">
                                <div><span>Catégorie</span><strong>{optionLabel(document.category)}</strong></div>
                                <div><span>Tags</span><strong>{(document.tags || []).map(optionLabel).join(", ") || "-"}</strong></div>
                                <div><span>Taille</span><strong>{formatBytes(document.sizeInBytes)}</strong></div>
                                <div><span>Extension</span><strong>{document.extension || document.fileExtension || "-"}</strong></div>
                                <div><span>Dernière modification</span><strong>{formatDate(document.updatedAt)}</strong></div>
                            </div>
                        </section>
                        {!isReviewMode && isCollaborator ? <KmsDocumentEngagement document={document} /> : null}
                        {!isReviewMode ? <KmsDocumentCommentsPanel document={document} /> : null}
                    </aside>
                </div>
            </main>
            <WorkflowDecisionModal
                open={Boolean(decision)}
                title={decision?.title}
                onClose={() => setDecision(null)}
                onConfirm={(comment) => {
                    if (decision?.action === "approve") dispatch(approveKmsDocumentRequest(document.id, comment));
                    if (decision?.action === "reject") dispatch(rejectKmsDocumentRequest(document.id, comment));
                    if (decision?.action === "return") dispatch(returnKmsDocumentToDraftRequest(document.id, comment));
                    setDecision(null);
                }}
            />
        </div>
    );
}

const getKmsFileHeaders = () => {
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const uid = sessionStorage.getItem("uid") || localStorage.getItem("uid");
    const tenantId = sessionStorage.getItem("selectedCompanyId") || localStorage.getItem("selectedCompanyId");
    const headers = { Accept: "*/*" };
    if (uid) headers.uid = String(uid);
    if (tenantId) headers["X-Tenant-Id"] = String(tenantId);
    if (token) headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
    return headers;
};

const toDocumentApiUrl = (raw) => {
    if (!raw) return "";
    return raw.startsWith("http") ? raw : `${API_BASE_URL}${raw.startsWith("/") ? "" : "/"}${raw}`;
};

async function openDocumentUrl(kmsDocument, mode) {
    const raw = mode === "download" ? getDownloadUrl(kmsDocument) : getPreviewUrl(kmsDocument);
    if (!raw) return;
    const url = toDocumentApiUrl(raw);
    try {
        const response = await fetch(url, { headers: getKmsFileHeaders() });
        if (!response.ok) throw new Error("Document non disponible");
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        if (mode === "download") {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = getDocumentTitle(kmsDocument);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            return;
        }
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
        Swal.fire({ icon: "error", text: "Document non disponible" });
    }
}

const confirmMoveContentToTrash = (dispatch, item) => {
    Swal.fire({
        icon: "warning",
        text: "Envoyer ce contenu ? la corbeille ?",
        showCancelButton: true,
        confirmButtonText: "Envoyer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
    }).then((result) => {
        if (result.isConfirmed) dispatch(deleteKmsContentRequest(item.id, itemRepertoireId(item)));
    });
};

const confirmMoveDocumentToTrash = (dispatch, item) => {
    Swal.fire({
        icon: "warning",
        text: "Envoyer ce document à la corbeille ?",
        showCancelButton: true,
        confirmButtonText: "Envoyer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
    }).then((result) => {
        if (result.isConfirmed) dispatch(deleteKmsDocumentRequest(item.id, itemRepertoireId(item), item.contentId));
    });
};

const confirmHardDeleteContent = (dispatch, item) => {
    Swal.fire({
        icon: "warning",
        text: "Supprimer définitivement cet élément ? Cette action est irréversible.",
        showCancelButton: true,
        confirmButtonText: "Supprimer définitivement",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
    }).then((result) => {
        if (result.isConfirmed) dispatch(hardDeleteKmsContentRequest(item.id, "trash"));
    });
};

const confirmHardDeleteDocument = (dispatch, item) => {
    Swal.fire({
        icon: "warning",
        text: "Supprimer définitivement cet élément ? Cette action est irréversible.",
        showCancelButton: true,
        confirmButtonText: "Supprimer définitivement",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#dc3545",
    }).then((result) => {
        if (result.isConfirmed) dispatch(hardDeleteKmsDocumentRequest(item.id, "trash"));
    });
};

function WorkflowDecisionModal({ open, title, onClose, onConfirm, requireComment = true }) {
    const [comment, setComment] = useState("");
    useEffect(() => { if (open) setComment(""); }, [open]);
    return (
        <KmsModal
            open={open}
            title={title}
            onClose={onClose}
            footer={
                <>
                    <button className="kms-btn" type="button" onClick={onClose}>Annuler</button>
                    <button className="kms-btn kms-btn--primary" type="button" disabled={requireComment && !comment.trim()} onClick={() => onConfirm(comment)}>
                        Confirmer
                    </button>
                </>
            }
        >
            {requireComment ? (
                <TextField label="Motif" value={comment} onChange={setComment} full textarea />
            ) : (
                <div className="kms-decision-confirm">Confirmer cette action  ?</div>
            )}
        </KmsModal>
    );
}

export function KmsContentManagementPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, currentUser, loadingUser, isRoleReady, isAdmin } = useKmsRole();
    const workspace = useDirectoryWorkspace();
    const { items, byRepertoire, listLoading, saveLoading, workflowHistoryByContent } = useSelector((state) => state.kmsContent || {});
    const { options } = useSelector((state) => state.kmsTaxonomy || {});
    const [filters, setFilters] = useState({ q: "", type: "", status: "", categoryId: "", tagId: "", author: "" });
    const [currentDirectory, setCurrentDirectory] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { dispatch(getKmsTaxonomyOptionsRequest()); }, [dispatch]);

    const directoryIds = useMemo(() => getAllDirectoryIds(workspace.tree), [workspace.tree]);
    useEffect(() => {
        directoryIds.forEach((id) => dispatch(getKmsContentsByRepertoireRequest(id)));
    }, [directoryIds.join(","), dispatch]);

    const allDirectoryContents = useMemo(() => directoryIds.flatMap((id) => byRepertoire?.[id] || []), [byRepertoire, directoryIds]);
    const rows = useMemo(() => {
        if (!currentDirectory) return [];
        const selectedId = String(currentDirectory.id);
        const source = byRepertoire?.[selectedId] || [];
        let result = source.length ? source : (items || []).filter((item) => String(itemRepertoireId(item) || "") === selectedId);
        result = result.filter((item) => !item.deleted && item.status !== "ARCHIVED");
        if (filters.type) result = result.filter((item) => item.type === filters.type);
        if (filters.status) result = result.filter((item) => item.status === filters.status);
        if (filters.categoryId) result = result.filter((item) => String(item.category?.id || "") === String(filters.categoryId));
        if (filters.tagId) result = result.filter((item) => (item.tags || []).some((tag) => String(tag.id) === String(filters.tagId)));
        if (filters.author) result = result.filter((item) => normalizeText(item.createdByFullName).includes(normalizeText(filters.author)));
        return filterByText(result, filters.q, [
            getContentTitle,
            (item) => item.summary,
            (item) => item.repertoireName,
            (item) => item.createdByFullName,
        ]);
    }, [byRepertoire, currentDirectory, filters, items]);

    useEffect(() => {
        rows.forEach((item) => {
            if (item?.id && !workflowHistoryByContent?.[item.id]) {
                dispatch(getKmsContentWorkflowHistoryRequest(item.id));
            }
        });
    }, [dispatch, rows, workflowHistoryByContent]);

    const patchFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const openEdit = (item) => { setEditing(item); setFormOpen(true); };
    const closeForm = () => { setEditing(null); setFormOpen(false); };
    const selectedPermissions = getUserPermissionForRepertoire(currentDirectory, user);
    const canCreateInSelected = Boolean(currentDirectory && (isAdmin || selectedPermissions.canCreate));

    if (loadingUser || !currentUser) {
        return <KmsPageLoading />;
    }

    if (currentUser && !isRoleReady) {
        return <KmsPageLoading message="Chargement du profil KMS..." spinner={false} />;
    }

    return (
        <div className="kms-page">
            <KmsPageHeader
                title="Gestion du contenu web"
                actions={canCreateInSelected ? <button className="kms-btn kms-btn--primary" type="button" onClick={() => setFormOpen(true)}><FileText size={15} />Créer contenu</button> : null}
            />
            <div className="kms-toolbar">
                <div className="kms-search-box"><Search size={14} /><input value={filters.q} onChange={(e) => patchFilter("q", e.target.value)} placeholder="Recherche..." /></div>
                <SelectField label="Type" value={filters.type} onChange={(v) => patchFilter("type", v)}><option value="">Tous</option>{CONTENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</SelectField>
                <SelectField label="Statut" value={filters.status} onChange={(v) => patchFilter("status", v)}><option value="">Tous</option>{["DRAFT","RETURNED_FOR_CORRECTION","SUBMITTED","APPROVED","REJECTED","PUBLISHED"].map((status) => <option key={status} value={status}>{status === "RETURNED_FOR_CORRECTION" ? "À corriger" : statusLabel(status)}</option>)}</SelectField>
                <SelectField label="Catégorie" value={filters.categoryId} onChange={(v) => patchFilter("categoryId", v)}><option value="">Toutes</option>{(options?.categories || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Tag" value={filters.tagId} onChange={(v) => patchFilter("tagId", v)}><option value="">Tous</option>{(options?.tags || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <TextField label="Auteur" value={filters.author} onChange={(v) => patchFilter("author", v)} />
            </div>
            <KmsDirectoryBrowser tree={workspace.tree} currentDirectory={currentDirectory} onRoot={() => setCurrentDirectory(null)} onSelect={setCurrentDirectory} query={filters.q} contents={allDirectoryContents} />
            {currentDirectory ? <div className="kms-card">
                <div className="kms-card-header"><h2>Contenus</h2><span>{listLoading ? "..." : `${rows.length} contenu(s)`}</span></div>
                <div className="kms-table-wrap">
                    <table className="kms-table">
                        <thead><tr><th>Titre</th><th>Type</th><th>Répertoire</th><th>Statut</th><th>Catégorie</th><th>Auteur</th><th>Mise à jour</th><th className="is-right">Actions</th></tr></thead>
                        <tbody>
                        {rows.length ? rows.map((item) => {
                            const status = String(item.status || "").toUpperCase();
                            const isDraft = status === "DRAFT";
                            const isRejected = status === "REJECTED";
                            const isSubmitted = status === "SUBMITTED";
                            const isPublished = status === "PUBLISHED";
                            const canEdit = isAdmin || hasItemRight(item, "canEdit");
                            const canSubmit = hasItemRight(item, "canSubmit");
                            const canArchive = isAdmin || hasItemRight(item, "canArchive");
                            const awaitingCorrection = isContentAwaitingCorrection(item, workflowHistoryByContent);
                            const correctionMeta = getContentCorrectionMeta(item, workflowHistoryByContent);
                            const rejectionMeta = getContentRejectionMeta(item, workflowHistoryByContent);
                            const statusNote = awaitingCorrection
                                ? getShortWorkflowComment(correctionMeta?.comment) || "Correction demandée"
                                : isRejected
                                    ? getShortWorkflowComment(rejectionMeta?.comment)
                                    : "";
                            const submitLabel = awaitingCorrection || isRejected ? "Resoumettre" : "Soumettre";
                            const submitComment = awaitingCorrection ? "Resoumission après correction" : "Soumission";
                            return (
                            <tr key={item.id}>
                                <td><div className="kms-row-title">{getContentTitle(item)}</div><div className="kms-row-sub">{item.summary || "-"}</div></td>
                                <td><KmsTypeBadge>{contentTypeLabel(item.type)}</KmsTypeBadge></td>
                                <td>{item.repertoireName || "-"}</td>
                                <td>
                                    <div className="kms-status-cell">
                                        {awaitingCorrection ? <span className="kms-correction-pill"><AlertCircle size={12} />À corriger</span> : <KmsStatusBadge status={item.status} />}
                                        {statusNote ? <span className="kms-status-note">{statusNote}</span> : null}
                                    </div>
                                </td>
                                <td>{optionLabel(item.category)}</td>
                                <td>{item.createdByFullName || "-"}</td>
                                <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                                <td className="is-right"><div className="kms-row-actions">
                                    <button className="kms-icon-btn" type="button" title="Voir détail" onClick={() => navigate(`/kms/contenus/${item.id}`)}><Eye size={14} /></button>
                                    {awaitingCorrection && canEdit ? <button className="kms-icon-btn kms-icon-btn--warning kms-icon-btn--pulse" type="button" title="À corriger" onClick={() => openEdit(item)}><AlertCircle size={14} /></button> : null}
                                    {canEdit && (isDraft || isRejected || isPublished || awaitingCorrection) && <button className="kms-icon-btn" type="button" title="Modifier" onClick={() => openEdit(item)}><Edit size={14} /></button>}
                                    {canSubmit && (isDraft || isRejected || awaitingCorrection) && <button className="kms-icon-btn" type="button" title={submitLabel} onClick={() => dispatch(submitKmsContentRequest(item.id, submitComment))}><Send size={14} /></button>}
                                    {canArchive && isPublished && <button className="kms-icon-btn" type="button" title="Archiver" onClick={() => dispatch(archiveKmsContentRequest(item.id, "Archivage", itemRepertoireId(item)))}><Archive size={14} /></button>}
                                    {canArchive && !isSubmitted && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Corbeille" onClick={() => confirmMoveContentToTrash(dispatch, item)}><Trash2 size={14} /></button>}
                                </div></td>
                            </tr>
                            );
                        }) : <tr><td colSpan={8}><KmsEmpty>Aucun contenu trouvé</KmsEmpty></td></tr>}
                        </tbody>
                    </table>
                </div>
            </div> : null}
            <ContentFormModal open={formOpen} onClose={closeForm} content={editing} tree={workspace.tree} currentUser={user} isAdmin={isAdmin} categories={options?.categories || []} tags={options?.tags || []} saveLoading={saveLoading} initialRepertoire={currentDirectory} />
        </div>
    );
}

export function KmsDocumentMediaManagementPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, currentUser, loadingUser, isRoleReady, isAdmin } = useKmsRole();
    const workspace = useDirectoryWorkspace();
    const { items, byRepertoire, listLoading, saveLoading } = useSelector((state) => state.kmsDocument || {});
    const contentsByRepertoire = useSelector((state) => state.kmsContent?.byRepertoire || {});
    const { options } = useSelector((state) => state.kmsTaxonomy || {});
    const [filters, setFilters] = useState({ q: "", documentType: "", status: "", categoryId: "", tagId: "", contentId: "", author: "" });
    const [currentDirectory, setCurrentDirectory] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { dispatch(getKmsTaxonomyOptionsRequest()); }, [dispatch]);
    const directoryIds = useMemo(() => getAllDirectoryIds(workspace.tree), [workspace.tree]);
    useEffect(() => {
        directoryIds.forEach((id) => {
            dispatch(getKmsDocumentsByRepertoireRequest(id));
            dispatch(getKmsContentsByRepertoireRequest(id));
        });
    }, [directoryIds.join(","), dispatch]);

    const contents = useMemo(() => directoryIds.flatMap((id) => contentsByRepertoire?.[id] || []), [contentsByRepertoire, directoryIds]);
    const allDirectoryDocuments = useMemo(() => directoryIds.flatMap((id) => byRepertoire?.[id] || []), [byRepertoire, directoryIds]);
    const rows = useMemo(() => {
        if (!currentDirectory) return [];
        const selectedId = String(currentDirectory.id);
        const source = byRepertoire?.[selectedId] || [];
        let result = source.length ? source : (items || []).filter((item) => String(itemRepertoireId(item) || "") === selectedId);
        result = result.filter((item) => !item.deleted && !item.archived);
        if (filters.documentType) result = result.filter((item) => item.documentType === filters.documentType);
        if (filters.status) result = result.filter((item) => item.status === filters.status);
        if (filters.categoryId) result = result.filter((item) => String(item.category?.id || "") === String(filters.categoryId));
        if (filters.tagId) result = result.filter((item) => (item.tags || []).some((tag) => String(tag.id) === String(filters.tagId)));
        if (filters.contentId) result = result.filter((item) => String(item.contentId || "") === String(filters.contentId));
        if (filters.author) result = result.filter((item) => normalizeText(item.uploadedByFullName).includes(normalizeText(filters.author)));
        return filterByText(result, filters.q, [getDocumentTitle, (item) => item.description, (item) => item.repertoireName, (item) => item.contentTitle]);
    }, [byRepertoire, currentDirectory, filters, items]);

    const patchFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const selectedPermissions = getUserPermissionForRepertoire(currentDirectory, user);
    const canCreateInSelected = Boolean(currentDirectory && (isAdmin || selectedPermissions.canCreate));

    if (loadingUser || !currentUser) {
        return <KmsPageLoading />;
    }

    if (currentUser && !isRoleReady) {
        return <KmsPageLoading message="Chargement du profil KMS..." spinner={false} />;
    }

    return (
        <div className="kms-page">
            <KmsPageHeader
                title="Gestion des documents et médias"
                actions={canCreateInSelected ? <button className="kms-btn kms-btn--primary" type="button" onClick={() => setFormOpen(true)}><Upload size={15} />Ajouter document/média</button> : null}
            />
            <div className="kms-toolbar">
                <div className="kms-search-box"><Search size={14} /><input value={filters.q} onChange={(e) => patchFilter("q", e.target.value)} placeholder="Recherche..." /></div>
                <SelectField label="Type fichier" value={filters.documentType} onChange={(v) => patchFilter("documentType", v)}><option value="">Tous</option>{DOCUMENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</SelectField>
                <SelectField label="Statut" value={filters.status} onChange={(v) => patchFilter("status", v)}><option value="">Tous</option>{["DRAFT","RETURNED_FOR_CORRECTION","SUBMITTED","APPROVED","REJECTED","PUBLISHED"].map((status) => <option key={status} value={status}>{status === "RETURNED_FOR_CORRECTION" ? "À corriger" : statusLabel(status)}</option>)}</SelectField>
                <SelectField label="Catégorie" value={filters.categoryId} onChange={(v) => patchFilter("categoryId", v)}><option value="">Toutes</option>{(options?.categories || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Tag" value={filters.tagId} onChange={(v) => patchFilter("tagId", v)}><option value="">Tous</option>{(options?.tags || []).map((item) => <option key={item.id} value={item.id}>{optionLabel(item)}</option>)}</SelectField>
                <SelectField label="Contenu lié" value={filters.contentId} onChange={(v) => patchFilter("contentId", v)}><option value="">Tous</option>{contents.map((item) => <option key={item.id} value={item.id}>{getContentTitle(item)}</option>)}</SelectField>
                <TextField label="Auteur" value={filters.author} onChange={(v) => patchFilter("author", v)} />
            </div>
            <KmsDirectoryBrowser tree={workspace.tree} currentDirectory={currentDirectory} onRoot={() => setCurrentDirectory(null)} onSelect={setCurrentDirectory} query={filters.q} documents={allDirectoryDocuments} kind="documents" />
            {currentDirectory ? <div className="kms-card">
                <div className="kms-card-header"><h2>Documents et médias</h2><span>{listLoading ? "..." : `${rows.length} document(s)`}</span></div>
                <DocumentTable rows={rows} isAdmin={isAdmin} readonly={!canCreateInSelected} onDetail={(item) => navigate(`/kms/documents/${item.id}`)} onEdit={(item) => { setEditing(item); setFormOpen(true); }} />
            </div> : null}
            <DocumentFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} document={editing} tree={workspace.tree} currentUser={user} isAdmin={isAdmin} categories={options?.categories || []} tags={options?.tags || []} contents={contents} saveLoading={saveLoading} initialRepertoire={currentDirectory} />
        </div>
    );
}

function DocumentTable({ rows, isAdmin, onDetail, onEdit, readonly = false, trash = false }) {
    const dispatch = useDispatch();
    return (
        <div className="kms-table-wrap">
            <table className="kms-table">
                <thead><tr><th>Nom</th><th>Type fichier</th><th>Répertoire</th><th>Statut</th><th>Catégorie</th><th>Auteur</th><th>Mise à jour</th><th className="is-right">Actions</th></tr></thead>
                <tbody>
                {rows.length ? rows.map((item) => {
                    const status = String(item.status || "DRAFT").toUpperCase();
                    const isDraft = status === "DRAFT";
                    const isRejected = status === "REJECTED";
                    const isPublished = status === "PUBLISHED";
                    const canManage = !readonly || isAdmin || hasItemRight(item, "canEdit");
                    const canArchive = isAdmin || hasItemRight(item, "canArchive") || !readonly;
                    const awaitingCorrection = isDocumentAwaitingCorrection(item);
                    const correctionMeta = getDocumentCorrectionMeta(item);
                    const rejectionMeta = getDocumentRejectionMeta(item);
                    const statusNote = awaitingCorrection
                        ? getShortWorkflowComment(correctionMeta?.comment) || "Correction demandée"
                        : isRejected
                            ? getShortWorkflowComment(rejectionMeta?.comment)
                            : "";
                    const submitLabel = awaitingCorrection || isRejected ? "Resoumettre" : "Soumettre";
                    const submitComment = awaitingCorrection ? "Resoumission après correction" : "Soumission";
                    return (
                        <tr key={item.id}>
                            <td><div className="kms-row-title">{getDocumentTitle(item)}</div><div className="kms-row-sub">{item.description || item.originalFilename || "-"}</div></td>
                            <td><KmsTypeBadge>{documentTypeLabel(item.documentType)}</KmsTypeBadge></td>
                            <td>{item.repertoireName || "-"}</td>
                            <td>
                                <div className="kms-status-cell">
                                    {awaitingCorrection ? <span className="kms-correction-pill"><AlertCircle size={12} />À corriger</span> : <KmsStatusBadge status={status} />}
                                    {statusNote ? <span className="kms-status-note">{statusNote}</span> : null}
                                </div>
                            </td>
                            <td>{optionLabel(item.category)}{(item.tags || []).length ? ` · ${(item.tags || []).map(optionLabel).join(", ")}` : ""}</td>
                            <td>{item.uploadedByFullName || "-"}</td>
                            <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                            <td className="is-right"><div className="kms-row-actions">
                                <button className="kms-icon-btn" type="button" title="Voir détail" onClick={() => onDetail(item)}><Eye size={14} /></button>
                                {(isPublished || canManage) && <button className="kms-icon-btn" type="button" title="Preview" onClick={() => openDocumentUrl(item, "preview")}><FileText size={14} /></button>}
                                {(isPublished || canManage) && <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(item, "download")}><Download size={14} /></button>}
                                {awaitingCorrection && canManage ? <button className="kms-icon-btn kms-icon-btn--warning kms-icon-btn--pulse" type="button" title="À corriger" onClick={() => onEdit(item)}><AlertCircle size={14} /></button> : null}
                                {canManage && (isDraft || isRejected || awaitingCorrection) && <button className="kms-icon-btn" type="button" title="Modifier" onClick={() => onEdit(item)}><Edit size={14} /></button>}
                                {canManage && (isDraft || isRejected || awaitingCorrection) && <button className="kms-icon-btn" type="button" title={submitLabel} onClick={() => dispatch(submitKmsDocumentRequest(item.id, submitComment))}><Send size={14} /></button>}
                                {canArchive && isPublished && <button className="kms-icon-btn" type="button" title="Archiver" onClick={() => dispatch(archiveKmsDocumentRequest(item.id, itemRepertoireId(item), item.contentId))}><Archive size={14} /></button>}
                                {canManage && (isDraft || isRejected || isPublished || awaitingCorrection) && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Corbeille" onClick={() => confirmMoveDocumentToTrash(dispatch, item)}><Trash2 size={14} /></button>}
                                {trash && isAdmin && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Suppression définitive" onClick={() => confirmHardDeleteDocument(dispatch, item)}><Trash2 size={14} /></button>}
                            </div></td>
                        </tr>
                    );
                }) : <tr><td colSpan={8}><KmsEmpty>Aucun document trouvé</KmsEmpty></td></tr>}
                </tbody>
            </table>
        </div>
    );
}

export function KmsContentWorkflowPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser, loadingUser, isRoleReady, isAdmin } = useKmsRole();
    const contentState = useSelector((state) => state.kmsContent || {});
    const documentState = useSelector((state) => state.kmsDocument || {});
    const [filters, setFilters] = useState({ q: "", type: "", nature: "", author: "" });
    const [activeTab, setActiveTab] = useState("ALL");
    const [decision, setDecision] = useState(null);

    useEffect(() => {
        dispatch(getKmsContentsRequest({}));
        dispatch(getKmsDocumentsRequest({}));
    }, [dispatch]);

    const rows = useMemo(() => {
        const statuses = ["SUBMITTED", "APPROVED", "REJECTED", "PUBLISHED"];
        const contents = (contentState.items || [])
            .filter((item) => statuses.includes(item.status))
            .map((item) => ({ ...item, __nature: "CONTENT", __natureLabel: "Contenu web", __title: getContentTitle(item), __typeLabel: contentTypeLabel(item.type), __author: item.createdByFullName }));
        const contentsToCorrect = (contentState.items || [])
            .filter((item) => isContentAwaitingCorrection(item, contentState.workflowHistoryByContent || {}))
            .map((item) => ({ ...item, __nature: "CONTENT", __natureLabel: "Contenu web", __title: getContentTitle(item), __typeLabel: contentTypeLabel(item.type), __author: item.createdByFullName, __workflowTab: "TO_CORRECT" }));
        const documents = (documentState.items || [])
            .map(normalizeDocumentResult)
            .filter((item) => statuses.includes(item.status) && !item.deleted && !item.archived)
            .map((item) => ({ ...item, __nature: "DOCUMENT", __natureLabel: "Document/Média", __title: getDocumentTitle(item), __typeLabel: documentTypeLabel(item.documentType), __author: item.uploadedByFullName }));
        const documentsToCorrect = (documentState.items || [])
            .map(normalizeDocumentResult)
            .filter((item) => !item.deleted && !item.archived && isDocumentAwaitingCorrection(item))
            .map((item) => ({ ...item, __nature: "DOCUMENT", __natureLabel: "Document/Média", __title: getDocumentTitle(item), __typeLabel: documentTypeLabel(item.documentType), __author: item.uploadedByFullName, __workflowTab: "TO_CORRECT" }));
        let result = uniqById([...contents, ...contentsToCorrect, ...documents, ...documentsToCorrect]);
        if (activeTab === "TO_CORRECT") result = result.filter((item) => item.__workflowTab === "TO_CORRECT");
        else if (activeTab !== "ALL") result = result.filter((item) => item.status === activeTab);
        if (filters.nature) result = result.filter((item) => item.__nature === filters.nature);
        if (filters.type) result = result.filter((item) => (item.type || item.documentType) === filters.type);
        if (filters.author) result = result.filter((item) => normalizeText(item.__author).includes(normalizeText(filters.author)));
        return filterByText(result, filters.q, [(item) => item.__title, (item) => item.repertoireName, (item) => item.__author]);
    }, [activeTab, contentState.items, contentState.workflowHistoryByContent, documentState.items, filters]);

    const confirmDecision = (comment) => {
        if (!decision) return;
        const id = decision.item.id || decision.item.contentId || decision.item.documentId;
        const isDocument = decision.item.__nature === "DOCUMENT";
        if (decision.action === "approve") dispatch(isDocument ? approveKmsDocumentRequest(id, comment) : approveKmsContentRequest(id, comment));
        if (decision.action === "reject") dispatch(isDocument ? rejectKmsDocumentRequest(id, comment) : rejectKmsContentRequest(id, comment));
        if (decision.action === "return") dispatch(isDocument ? returnKmsDocumentToDraftRequest(id, comment) : returnKmsContentToDraftRequest(id, comment));
        if (decision.action === "publish") dispatch(isDocument ? publishKmsDocumentRequest(id, comment) : publishKmsContentRequest(id, comment));
        setDecision(null);
    };

    const openDetails = (item) => {
        const id = item.id || item.contentId || item.documentId;
        if (!id) {
            Swal.fire({ icon: "error", text: "Détail indisponible", confirmButtonText: "OK" });
            return;
        }
        navigate(item.__nature === "DOCUMENT" ? "/kms/documents/" + id + "?viewMode=review" : "/kms/contenus/" + id + "?viewMode=review");
    };

    const loading = contentState.listLoading || documentState.listLoading;
    const typeOptions = filters.nature === "DOCUMENT" ? DOCUMENT_TYPES : filters.nature === "CONTENT" ? CONTENT_TYPES : [...CONTENT_TYPES, ...DOCUMENT_TYPES];
    const allWorkflowRows = useMemo(() => {
        const statuses = ["SUBMITTED", "APPROVED", "REJECTED", "PUBLISHED"];
        const contents = (contentState.items || [])
            .filter((item) => statuses.includes(item.status))
            .map((item) => ({ ...item, __nature: "CONTENT" }));
        const contentsToCorrect = (contentState.items || [])
            .filter((item) => isContentAwaitingCorrection(item, contentState.workflowHistoryByContent || {}))
            .map((item) => ({ ...item, __nature: "CONTENT", __workflowTab: "TO_CORRECT" }));
        const documents = (documentState.items || [])
            .map(normalizeDocumentResult)
            .filter((item) => statuses.includes(item.status) && !item.deleted && !item.archived)
            .map((item) => ({ ...item, __nature: "DOCUMENT" }));
        const documentsToCorrect = (documentState.items || [])
            .map(normalizeDocumentResult)
            .filter((item) => !item.deleted && !item.archived && isDocumentAwaitingCorrection(item))
            .map((item) => ({ ...item, __nature: "DOCUMENT", __workflowTab: "TO_CORRECT" }));
        return uniqById([...contents, ...contentsToCorrect, ...documents, ...documentsToCorrect]);
    }, [contentState.items, contentState.workflowHistoryByContent, documentState.items]);
    const counts = useMemo(() => ({
        ALL: allWorkflowRows.length,
        SUBMITTED: allWorkflowRows.filter((item) => item.status === "SUBMITTED").length,
        APPROVED: allWorkflowRows.filter((item) => item.status === "APPROVED").length,
        REJECTED: allWorkflowRows.filter((item) => item.status === "REJECTED").length,
        TO_CORRECT: allWorkflowRows.filter((item) => item.__workflowTab === "TO_CORRECT").length,
        PUBLISHED: allWorkflowRows.filter((item) => item.status === "PUBLISHED").length,
    }), [allWorkflowRows]);

    if (loadingUser || !currentUser) {
        return <KmsPageLoading />;
    }

    if (currentUser && !isRoleReady) {
        return <KmsPageLoading message="Chargement du profil KMS..." spinner={false} />;
    }

    return (
        <div className="training-treatment-page tdPage">
           <div><h2>Traitement </h2></div>
            <div className="training-treatment-filters">
                <div className="treatment-filter-search"><Search size={16} className="search-icon" /><input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Rechercher par titre, répertoire ou auteur" /></div>
                <SelectField label="Nature" value={filters.nature} onChange={(v) => setFilters({ ...filters, nature: v, type: "" })}><option value="">Toutes</option><option value="CONTENT">Contenu web</option><option value="DOCUMENT">Document/Média</option></SelectField>
                <SelectField label="Type" value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })}><option value="">Tous</option>{typeOptions.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</SelectField>
                <TextField label="Auteur" value={filters.author} onChange={(v) => setFilters({ ...filters, author: v })} />
            </div>
            <div className="training-treatment-table-card tdCard tdCard--management">
                <div className="treatment-tabs">
                    {[
                        ["ALL", "Tous", AlertCircle],
                        ["SUBMITTED", "Soumis", Send],
                        ["APPROVED", "Approuvés", Check],
                        ["REJECTED", "Rejetés", X],
                        ["TO_CORRECT", "À corriger", RotateCcw],
                        ["PUBLISHED", "Publiés", Upload],
                    ].map(([key, label, Icon]) => (
                        <button key={key} type="button" className={`treatment-tab ${activeTab === key ? "is-active" : ""}`} onClick={() => setActiveTab(key)}>
                            <Icon size={16} />{label}<span className={`treatment-tab-count ${activeTab === key ? "is-active" : ""}`}>{counts[key]}</span>
                        </button>
                    ))}
                </div>
                {/*<div className="kms-card-header"><h2>Publications à traiter</h2><span>{loading ? "..." : rows.length + " élément(s)"}</span></div>*/}
                <div className="tdTableWrap"><table className="tdTable tdTable--collab">
                    <thead><tr><th>Nature</th><th>Titre / Nom</th><th>Type</th><th>Répertoire</th><th>Auteur</th><th>Date</th><th>Statut</th><th className="is-right">Actions</th></tr></thead>
                    <tbody>{rows.length ? rows.map((item) => {
                        const status = item.status;
                        const isSubmitted = status === "SUBMITTED";
                        const isApproved = status === "APPROVED";
                        const canProcess = isAdmin || hasItemRight(item, "canValidate");
                        const canPublish = isAdmin || hasItemRight(item, "canPublish");
                        return (
                            <tr key={`${item.__nature}-${item.id}`}>
                                <td>{item.__natureLabel}</td>
                                <td><div className="kms-row-title">{item.__title}</div></td>
                                <td><KmsTypeBadge>{item.__typeLabel}</KmsTypeBadge></td>
                                <td>{item.repertoireName || "-"}</td>
                                <td>{item.__author || "-"}</td>
                                <td>{formatDate(item.submittedAt || item.updatedAt)}</td>
                                <td><KmsStatusBadge status={status} /></td>
                                <td className="is-right"><div className="kms-row-actions">
                                    <button className="tdIconBtn" type="button" title="Voir détail" onClick={() => openDetails(item)}><Eye size={16} /></button>
                                    {canProcess && isSubmitted && <button className="tdIconBtn tdIconBtn--success" type="button" title="Valider" onClick={() => setDecision({ item, action: "approve", title: "Valider la publication" })}><Check size={16} /></button>}
                                    {canProcess && isSubmitted && <button className="tdIconBtn tdIconBtn--danger" type="button" title="Rejeter" onClick={() => setDecision({ item, action: "reject", title: "Rejeter la publication" })}><X size={16} /></button>}
                                    {canProcess && isSubmitted && <button className="tdIconBtn tdIconBtn--warning" type="button" title="Retour correction" onClick={() => setDecision({ item, action: "return", title: "Retour correction" })}><RotateCcw size={16} /></button>}
                                    {canPublish && isApproved && <button className="tdIconBtn" type="button" title="Publier" onClick={() => setDecision({ item, action: "publish", title: "Publier la publication" })}><Upload size={16} /></button>}
                                </div></td>
                            </tr>
                        );
                    }) : <tr><td colSpan={8}><KmsEmpty>Aucune publication ? traiter</KmsEmpty></td></tr>}</tbody>
                </table></div>
            </div>
            <WorkflowDecisionModal open={Boolean(decision)} title={decision?.title} onClose={() => setDecision(null)} onConfirm={confirmDecision} />
        </div>
    );
}
export function KmsArchivePage() {
    const dispatch = useDispatch();
    const { currentUser, loadingUser, isRoleReady, isAdmin } = useKmsRole();
    const { archivedItems: contents } = useSelector((state) => state.kmsContent || {});
    const { archivedItems: documents } = useSelector((state) => state.kmsDocument || {});
    useEffect(() => { dispatch(getKmsArchivedContentsRequest()); dispatch(getKmsArchivedDocumentsRequest()); }, [dispatch]);
    if (loadingUser || !currentUser) {
        return <KmsPageLoading />;
    }
    if (currentUser && !isRoleReady) {
        return <KmsPageLoading message="Chargement du profil KMS..." spinner={false} />;
    }
    return <ArchiveTrashPage title="Archivage" contents={contents} documents={documents} archive isAdmin={isAdmin} />;
}

export function KmsTrashPage() {
    const dispatch = useDispatch();
    const { currentUser, loadingUser, isRoleReady, isAdmin } = useKmsRole();
    const { trashedItems: contents } = useSelector((state) => state.kmsContent || {});
    const { trashedItems: documents } = useSelector((state) => state.kmsDocument || {});
    useEffect(() => { dispatch(getKmsTrashedContentsRequest()); dispatch(getKmsTrashedDocumentsRequest()); }, [dispatch]);
    if (loadingUser || !currentUser) {
        return <KmsPageLoading />;
    }
    if (currentUser && !isRoleReady) {
        return <KmsPageLoading message="Chargement du profil KMS..." spinner={false} />;
    }
    return <ArchiveTrashPage title="Corbeille" contents={contents} documents={documents} trash isAdmin={isAdmin} />;
}

function ArchiveTrashPage({ title, contents = [], documents = [], archive = false, trash = false, isAdmin = false }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [activeTab, setActiveTab] = useState("contents");
    const filteredContents = filterByText(contents, q, [getContentTitle, (item) => item.repertoireName]);
    const filteredDocuments = filterByText(documents, q, [getDocumentTitle, (item) => item.repertoireName, (item) => item.contentTitle]);
    return (
        <div className="kms-page">
            <KmsPageHeader title={title} />
            <div className="kms-toolbar">
                <div className="kms-search-box"><Search size={14} /><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Recherche..." /></div>
            </div>
            <div className="kms-tabs">
                <button
                    className={`kms-tab ${activeTab === "contents" ? "is-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab("contents")}
                >
                    Contenus web <span className="kms-tab-count">{filteredContents.length}</span>
                </button>
                <button
                    className={`kms-tab ${activeTab === "documents" ? "is-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab("documents")}
                >
                    Documents & Médias <span className="kms-tab-count">{filteredDocuments.length}</span>
                </button>
            </div>
            <div className="kms-card">
                {activeTab === "contents" ? (
                    <>
                        <div className="kms-card-header"><h2>Contenus web</h2><span>{filteredContents.length} contenu(s)</span></div>
                        <div className="tdTableWrap"><table className="tdTable tdTable--collab">
                            <thead><tr><th>Titre</th><th>Type</th><th>Répertoire</th><th>Statut</th><th>Auteur</th><th>Date archivage</th><th className="is-right">Actions</th></tr></thead>
                            <tbody>{filteredContents.length ? filteredContents.map((item) => (
                                <tr key={item.id}>
                                    <td><div className="kms-row-title">{getContentTitle(item)}</div></td>
                                    <td><KmsTypeBadge>{contentTypeLabel(item.type)}</KmsTypeBadge></td>
                                    <td>{item.repertoireName || "-"}</td>
                                    <td><KmsStatusBadge status={archive ? "ARCHIVED" : "DELETED"} /></td>
                                    <td>{item.createdByFullName || "-"}</td>
                                    <td>{formatDate(archive ? item.updatedAt : item.deletedAt)}</td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Voir détail" onClick={() => navigate(`/kms/contenus/${item.id}`)}><Eye size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title="Restaurer" onClick={() => dispatch(archive ? restoreKmsContentFromArchiveRequest(item.id) : restoreKmsContentFromTrashRequest(item.id))}><RotateCcw size={14} /></button>
                                        {trash && isAdmin && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Suppression définitive" onClick={() => confirmHardDeleteContent(dispatch, item)}><Trash2 size={14} /></button>}
                                    </div></td>
                                </tr>
                            )) : <tr><td colSpan={7}><KmsEmpty>Aucun contenu</KmsEmpty></td></tr>}</tbody>
                        </table></div>
                    </>
                ) : (
                    <>
                        <div className="kms-card-header"><h2>Documents & Médias</h2><span>{filteredDocuments.length} document(s)</span></div>
                        <div className="tdTableWrap"><table className="tdTable tdTable--collab">
                            <thead><tr><th>Nom</th><th>Type fichier</th><th>Répertoire</th><th>Contenu lié</th><th>Auteur</th><th>Date archivage</th><th className="is-right">Actions</th></tr></thead>
                            <tbody>{filteredDocuments.length ? filteredDocuments.map((item) => (
                                <tr key={item.id}>
                                    <td><div className="kms-row-title">{getDocumentTitle(item)}</div></td>
                                    <td><KmsTypeBadge>{documentTypeLabel(item.documentType)}</KmsTypeBadge></td>
                                    <td>{item.repertoireName || "-"}</td>
                                    <td>{item.contentTitle || "-"}</td>
                                    <td>{item.uploadedByFullName || "-"}</td>
                                    <td>{formatDate(archive ? item.archivedAt : item.deletedAt)}</td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Voir détail" onClick={() => navigate(`/kms/documents/${item.id}`)}><Eye size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title="Preview" onClick={() => openDocumentUrl(item, "preview")}><FileText size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title="Télécharger" onClick={() => openDocumentUrl(item, "download")}><Download size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title="Restaurer" onClick={() => dispatch(archive ? restoreKmsDocumentFromArchiveRequest(item.id) : restoreKmsDocumentFromTrashRequest(item.id))}><RotateCcw size={14} /></button>
                                        {trash && isAdmin && <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Suppression définitive" onClick={() => confirmHardDeleteDocument(dispatch, item)}><Trash2 size={14} /></button>}
                                    </div></td>
                                </tr>
                            )) : <tr><td colSpan={7}><KmsEmpty>Aucun document</KmsEmpty></td></tr>}</tbody>
                        </table></div>
                    </>
                )}
            </div>
        </div>
    );
}

export function KmsTaxonomyPage() {
    const dispatch = useDispatch();
    const { categories, tags, loading, saving } = useSelector((state) => state.kmsTaxonomy || {});
    const [activeTab, setActiveTab] = useState("categories");
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [categoryForm, setCategoryForm] = useState({ id: null, name: "", description: "", active: true });
    const [tagForm, setTagForm] = useState({ id: null, name: "" });

    useEffect(() => { dispatch(getKmsCategoriesRequest(false)); dispatch(getKmsTagsRequest()); }, [dispatch]);

    const openCategoryModal = (category = null) => {
        setCategoryForm(
            category
                ? {
                    id: category.id,
                    name: category.name || "",
                    description: category.description || "",
                    active: category.active !== false,
                }
                : { id: null, name: "", description: "", active: true }
        );
        setCategoryModalOpen(true);
    };

    const openTagModal = (tag = null) => {
        setTagForm(tag ? { id: tag.id, name: tag.name || "" } : { id: null, name: "" });
        setTagModalOpen(true);
    };

    const closeCategoryModal = () => {
        setCategoryModalOpen(false);
        setCategoryForm({ id: null, name: "", description: "", active: true });
    };

    const closeTagModal = () => {
        setTagModalOpen(false);
        setTagForm({ id: null, name: "" });
    };

    const saveCategory = () => {
        if (!categoryForm.name.trim()) return;
        const data = { name: categoryForm.name, description: categoryForm.description, active: categoryForm.active };
        if (categoryForm.id) dispatch(updateKmsCategoryRequest(categoryForm.id, data));
        else dispatch(createKmsCategoryRequest(data));
        closeCategoryModal();
    };

    const saveTag = () => {
        if (!tagForm.name.trim()) return;
        if (tagForm.id) dispatch(updateKmsTagRequest(tagForm.id, { name: tagForm.name }));
        else dispatch(createKmsTagRequest({ name: tagForm.name }));
        closeTagModal();
    };

    const confirmDeleteCategory = (category) => {
        Swal.fire({
            icon: "warning",
            text: "Supprimer cette catégorie ?",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
        }).then((result) => {
            if (result.isConfirmed) dispatch(deleteKmsCategoryRequest(category.id));
        });
    };

    const confirmDeleteTag = (tag) => {
        Swal.fire({
            icon: "warning",
            text: "Supprimer ce tag ?",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
        }).then((result) => {
            if (result.isConfirmed) dispatch(deleteKmsTagRequest(tag.id));
        });
    };

    return (
        <div className="kms-page">
            <KmsPageHeader title="Taxonomie" />
            <div className="kms-tabs">
                <button
                    className={`kms-tab ${activeTab === "categories" ? "is-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab("categories")}
                >
                    Catégories <span className="kms-tab-count">{categories.length}</span>
                </button>
                <button
                    className={`kms-tab ${activeTab === "tags" ? "is-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab("tags")}
                >
                    Tags <span className="kms-tab-count">{tags.length}</span>
                </button>
            </div>
            <div className="kms-card">
                {activeTab === "categories" ? (
                    <>
                        <div className="kms-card-header">
                            <h2>Catégories</h2>
                            <div className="kms-header-actions">
                                <span>{loading ? "..." : `${categories.length} catégorie(s)`}</span>
                                <button className="kms-btn kms-btn--primary" type="button" onClick={() => openCategoryModal()}>
                                    Ajouter catégorie
                                </button>
                            </div>
                        </div>
                        <div className="tdTableWrap"><table className="tdTable tdTable--collab">
                            <thead><tr><th>Nom</th><th>Description</th><th>Statut</th><th className="is-right">Actions</th></tr></thead>
                            <tbody>{categories.length ? categories.map((item) => (
                                <tr key={item.id}>
                                    <td><div className="kms-row-title">{optionLabel(item)}</div></td>
                                    <td>{item.description || "-"}</td>
                                    <td><KmsStatusBadge status={item.active === false ? "INACTIVE" : "ACTIVE"} /></td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Modifier" onClick={() => openCategoryModal(item)}><Edit size={14} /></button>
                                        <button className="kms-icon-btn" type="button" title={item.active === false ? "Activer" : "Désactiver"} onClick={() => dispatch(toggleKmsCategoryActiveRequest(item.id, item.active === false))}><RotateCcw size={14} /></button>
                                        <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Supprimer" onClick={() => confirmDeleteCategory(item)}><Trash2 size={14} /></button>
                                    </div></td>
                                </tr>
                            )) : <tr><td colSpan={4}><KmsEmpty>Aucune catégorie</KmsEmpty></td></tr>}</tbody>
                        </table></div>
                    </>
                ) : (
                    <>
                        <div className="kms-card-header">
                            <h2>Tags</h2>
                            <div className="kms-header-actions">
                                <span>{loading ? "..." : `${tags.length} tag(s)`}</span>
                                <button className="kms-btn kms-btn--primary" type="button" onClick={() => openTagModal()}>
                                    Ajouter tag
                                </button>
                            </div>
                        </div>
                        <div className="tdTableWrap"><table className="tdTable tdTable--collab">
                            <thead><tr><th>Nom</th><th className="is-right">Actions</th></tr></thead>
                            <tbody>{tags.length ? tags.map((item) => (
                                <tr key={item.id}>
                                    <td><div className="kms-row-title">{optionLabel(item)}</div></td>
                                    <td className="is-right"><div className="kms-row-actions">
                                        <button className="kms-icon-btn" type="button" title="Modifier" onClick={() => openTagModal(item)}><Edit size={14} /></button>
                                        <button className="kms-icon-btn kms-icon-btn--danger" type="button" title="Supprimer" onClick={() => confirmDeleteTag(item)}><Trash2 size={14} /></button>
                                    </div></td>
                                </tr>
                            )) : <tr><td colSpan={2}><KmsEmpty>Aucun tag</KmsEmpty></td></tr>}</tbody>
                        </table></div>
                    </>
                )}
            </div>
            <KmsModal
                open={categoryModalOpen}
                title={categoryForm.id ? "Modifier catégorie" : "Ajouter catégorie"}
                onClose={closeCategoryModal}
                footer={
                    <>
                        <button className="kms-btn" type="button" onClick={closeCategoryModal}>Annuler</button>
                        <button className="kms-btn kms-btn--primary" type="button" onClick={saveCategory} disabled={saving || !categoryForm.name.trim()}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="kms-form-grid">
                    <TextField label="Nom" value={categoryForm.name} onChange={(v) => setCategoryForm({ ...categoryForm, name: v })} />
                    <TextField label="Description" value={categoryForm.description} onChange={(v) => setCategoryForm({ ...categoryForm, description: v })} full textarea />
                </div>
            </KmsModal>
            <KmsModal
                open={tagModalOpen}
                title={tagForm.id ? "Modifier tag" : "Ajouter tag"}
                onClose={closeTagModal}
                footer={
                    <>
                        <button className="kms-btn" type="button" onClick={closeTagModal}>Annuler</button>
                        <button className="kms-btn kms-btn--primary" type="button" onClick={saveTag} disabled={saving || !tagForm.name.trim()}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="kms-form-grid">
                    <TextField label="Nom" value={tagForm.name} onChange={(v) => setTagForm({ ...tagForm, name: v })} full />
                </div>
            </KmsModal>
        </div>
    );
}





